import React, { useEffect, useMemo, useRef, useState } from 'react';
import apiClient, { buildApiUrl } from '../config/api';
import MsgReader from '@kenjiuno/msgreader';
import { PDFDocument } from 'pdf-lib';
import { Document, Page, pdfjs } from 'react-pdf';
import PostalMime from 'postal-mime';
import * as XLSX from 'xlsx';
import Navbar from '../components/Navbar';
import HomeDocumentViewer from '../components/HomeDocumentViewer';
import AppearanceSelector from '../components/document-viewer/AppearanceSelector';
import DarkModePdfCanvas from '../components/document-viewer/DarkModePdfCanvas';
import DocumentMetadata from '../components/document-viewer/DocumentMetadata';
import PdfSideNavigation from '../components/document-viewer/PdfSideNavigation';
import { getExcelPreviewDisabledReason, MAX_EXCEL_PREVIEW_BYTES } from '../utils/documentPreview';
import { styles } from '../styles/newstyles';
import '../styles/OwnerDetail.css';
import '../styles/cao.css';
import 'react-pdf/dist/Page/TextLayer.css';

const DEFAULT_YEAR = 2026;
const CHIEF_CATEGORY_ID = 4;      // fixed category "Chief"
const CONTRACTS_CATEGORY_ID = 5;  // fixed category "Contracts"
const HIDDEN_NON_ADMIN_PROTEST_TYPE_IDS = new Set([4, 5]);
const UP_ARROW = '\u25B2';
const DOWN_ARROW = '\u25BC';

const FILE_TYPE_LABELS = {
  'application/pdf': 'PDF',
  'image/*': 'Image',
  'image/jpg': 'Image',
  'application/msword': 'Word',
  'application/doc': 'Word',
  'application/docx': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/xls': 'Excel',
  'application/xlsx': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'message/rfc822': 'Email',
  'application/vnd.ms-outlook': 'Outlook Email',
  'application/zip': 'ZIP'
};

const EXTENSION_LABELS = {
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  xls: 'Excel',
  xlsx: 'Excel',
  png: 'Image',
  jpg: 'Image',
  jpeg: 'Image',
  tif: 'Image',
  tiff: 'Image',
  eml: 'Email',
  msg: 'Outlook Email',
  zip: 'ZIP'
};

const EXTENSION_MIME_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  eml: 'message/rfc822',
  msg: 'application/vnd.ms-outlook',
  zip: 'application/zip'
};

const PREVIEWABLE_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff', 'xls', 'xlsx', 'eml', 'msg']);
const OPEN_ACTION = 'open';
const DOWNLOAD_ACTION = 'download';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US');
const EMAIL_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
});
const DEFAULT_PDF_SCALE = 0.5;
const MOBILE_DEFAULT_PDF_SCALE = 1;
const MIN_PDF_SCALE = 0.3;
const MAX_PDF_SCALE = 2.5;
const PDF_SCALE_STEP = 0.1;
const LARGE_PDF_PAGE_THRESHOLD = 100;
const CHIEF_NOTICE_OF_VALUE_TYPE_ID = 68;
const PDF_OWNER_INDEX_BATCH_SIZE = 20;
const PDF_OWNER_SEARCH_RESULT_LIMIT = 12;
const PDF_OWNER_ROW_TOP_RATIO = 0.5;
const PDF_OWNER_LEFT_X_RATIO = 0.36;

const getDefaultDocumentViewerScale = () => (
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    ? MOBILE_DEFAULT_PDF_SCALE
    : DEFAULT_PDF_SCALE
);

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const pdfOwnerIndexCache = new Map();
const createEmptyViewerState = () => ({
  open: false,
  previewUrl: '',
  fileName: '',
  fileTypeLabel: '',
  mimeType: '',
  backLabel: 'Back',
  resource: null
});

const createEmptyPdfOwnerIndexState = () => ({
  status: 'idle',
  scannedPages: 0,
  totalPages: 0,
  entries: [],
  error: ''
});

const getPdfOwnerResultKey = (entry) => {
  if (!entry) return '';
  return `${entry.displayName}-${entry.startPage}-${entry.endPage}`;
};

const sanitizeDownloadFileSegment = (value, fallback = 'owner') => {
  const normalized = String(value || '')
    .normalize('NFKD')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return normalized || fallback;
};

const buildPdfOwnerRangeFileName = (fileName, entry) => {
  return buildPdfPageRangeFileName(
    fileName,
    Number(entry?.startPage || 1),
    Number(entry?.endPage || entry?.startPage || 1),
    sanitizeDownloadFileSegment(entry?.displayName, 'owner').slice(0, 48)
  );
};

const buildPdfPageRangeFileName = (fileName, startPage, endPage, prefixSegment = '') => {
  const trimmedFileName = String(fileName || '').trim() || 'document.pdf';
  const pageSegment = `pages-${Number(startPage || 1)}-${Number(endPage || startPage || 1)}`;
  const suffix = prefixSegment ? `${prefixSegment}-${pageSegment}` : pageSegment;

  if (/\.pdf$/i.test(trimmedFileName)) {
    return trimmedFileName.replace(/\.pdf$/i, `-${suffix}.pdf`);
  }

  return `${trimmedFileName}-${suffix}.pdf`;
};

const renderFileActionIcon = (action) => {
  if (action === OPEN_ACTION) {
    return (
      <svg
        className="cao-file-actions__icon"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M1.5 12s3.8-6.5 10.5-6.5S22.5 12 22.5 12s-3.8 6.5-10.5 6.5S1.5 12 1.5 12Z" />
        <circle cx="12" cy="12" r="3.25" />
      </svg>
    );
  }

  return (
    <svg
      className="cao-file-actions__icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3.5v10" />
      <path d="m8.25 10.5 3.75 3.75 3.75-3.75" />
      <path d="M5 18.5h14" />
    </svg>
  );
};

const renderPdfRangeActionIcon = (action) => {
  if (action === 'download') {
    return (
      <svg
        className="cao-document-viewer__pdf-range-icon"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3.5v10" />
        <path d="m8.25 10.5 3.75 3.75 3.75-3.75" />
        <path d="M5 18.5h14" />
      </svg>
    );
  }

  return (
    <svg
      className="cao-document-viewer__pdf-range-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 8.5V4.5h10v4" />
      <path d="M7 17.5h10v2H7z" />
      <path d="M6 9.5h12a2 2 0 0 1 2 2v4H4v-4a2 2 0 0 1 2-2Z" />
      <path d="M16.5 12.5h.01" />
    </svg>
  );
};

const renderPdfPagePlaceholder = () => (
  <div className="cao-document-viewer__page-placeholder" aria-hidden="true">
    <span className="cao-document-viewer__page-placeholder-line" />
    <span className="cao-document-viewer__page-placeholder-line cao-document-viewer__page-placeholder-line--short" />
  </div>
);

const normalizeFileExtension = (fileExtension, fileName) => {
  const trimmedExtension = (fileExtension || '').toString().trim().replace(/^\.+/, '').toLowerCase();
  if (trimmedExtension) return trimmedExtension;
  if (!fileName) return '';

  const match = /\.([^.]+)$/.exec(String(fileName).trim());
  return match?.[1]?.toLowerCase() || '';
};

const getResolvedFileName = (fileName, fileExtension, fallbackBaseName) => {
  const trimmedFileName = (fileName || '').toString().trim();
  if (trimmedFileName) return trimmedFileName;

  const extension = normalizeFileExtension(fileExtension, fileName);
  return extension ? `${fallbackBaseName}.${extension}` : fallbackBaseName;
};

const isUnknownMimeType = (fileType) => {
  const normalized = (fileType || '').toString().trim().toLowerCase();
  return !normalized || normalized === 'application/octet-stream' || normalized.startsWith('unknown/');
};

const resolveMimeType = (fileType, fileName, fileExtension) => {
  const normalized = (fileType || '').toString().trim().toLowerCase();
  if (normalized === 'image/jpg') return 'image/jpeg';
  if (!isUnknownMimeType(normalized)) return normalized;

  const extension = normalizeFileExtension(fileExtension, fileName);
  return EXTENSION_MIME_TYPES[extension] || 'application/octet-stream';
};

const normalizeViewerText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const normalizeViewerSearchValue = (value) => normalizeViewerText(value).toLowerCase();

const isLikelyOwnerAddressLine = (value) => {
  const normalized = normalizeViewerText(value).toUpperCase();
  if (!normalized) return false;
  if (/^(PO BOX|P O BOX|P\.O\. BOX|RR\b|HC\b|PMB\b)/.test(normalized)) return true;
  if (/\bTX\b\s+\d{5}(?:-\d{4})?$/.test(normalized)) return true;
  if (/^\d+\s+/.test(normalized) && /\b(RD|ROAD|ST|STREET|AVE|AVENUE|DR|DRIVE|LN|LANE|BLVD|HIGHWAY|HWY|COURT|CT|CIR|CIRCLE|PLAZA|PLZ|WAY|TRL|TRAIL|PKWY|PARKWAY|STE|SUITE|UNIT|APT)\b/.test(normalized)) {
    return true;
  }
  return false;
};

const buildPdfOwnerNoticeRows = (items, viewportWidth, viewportHeight) => {
  const rows = new Map();
  const topThreshold = viewportHeight * PDF_OWNER_ROW_TOP_RATIO;

  items.forEach((item) => {
    const text = normalizeViewerText(item?.str);
    if (!text) return;

    const transform = Array.isArray(item?.transform) ? item.transform : [];
    const x = Number(transform[4] || 0);
    const y = Number(transform[5] || 0);
    if (y < topThreshold) return;

    const rowKey = Math.round(y / 4) * 4;
    const parts = rows.get(rowKey) || [];
    parts.push({ x, text });
    rows.set(rowKey, parts);
  });

  return [...rows.entries()]
    .sort((left, right) => Number(right[0]) - Number(left[0]))
    .map(([y, parts]) => {
      const orderedParts = [...parts].sort((left, right) => left.x - right.x);
      return {
        y: Number(y),
        fullText: normalizeViewerText(orderedParts.map((part) => part.text).join(' ')),
        leftText: normalizeViewerText(
          orderedParts
            .filter((part) => part.x < viewportWidth * PDF_OWNER_LEFT_X_RATIO)
            .map((part) => part.text)
            .join(' ')
        )
      };
    });
};

const extractPdfOwnerNoticeEntryFromRows = (rows) => {
  const hasHeader = rows.some((row) => /NOTICE OF APPRAISED VALUE/i.test(row.fullText));
  const dateIndex = rows.findIndex((row) => /Date of Notice:/i.test(row.fullText));
  const dearIndex = rows.findIndex((row) => /Dear Property Owner:/i.test(row.fullText));
  if (!hasHeader || dateIndex === -1 || dearIndex === -1 || dearIndex <= dateIndex) return null;

  const ownerLines = rows
    .slice(dateIndex + 1, dearIndex)
    .map((row) => row.leftText)
    .filter(Boolean)
    .filter((line) => !/^\d{6,}$/.test(line));

  if (!ownerLines.length) return null;

  const ownerNameLines = [];
  ownerLines.forEach((line) => {
    if (ownerNameLines.length > 0 && isLikelyOwnerAddressLine(line)) return;
    if (!ownerNameLines.length && isLikelyOwnerAddressLine(line)) return;
    if (!ownerNameLines.length || !isLikelyOwnerAddressLine(line)) {
      ownerNameLines.push(line);
    }
  });

  const displayName = normalizeViewerText(
    (ownerNameLines.length ? ownerNameLines : ownerLines.slice(0, 2)).join(' / ')
  );
  if (!displayName) return null;

  return {
    displayName,
    ownerLines,
    searchText: normalizeViewerSearchValue([displayName, ...ownerLines].join(' '))
  };
};

export const extractPdfOwnerNoticeEntry = async (pdfDocument, pageNumber) => {
  const pdfPage = await pdfDocument.getPage(pageNumber);
  const viewport = pdfPage.getViewport({ scale: 1 });
  const textContent = await pdfPage.getTextContent();
  const rows = buildPdfOwnerNoticeRows(textContent.items || [], viewport.width, viewport.height);
  return extractPdfOwnerNoticeEntryFromRows(rows);
};

const getPdfOwnerIndexCacheKey = (resource) => {
  if (!resource) return '';
  if (resource.localBlob instanceof Blob) {
    return `blob:${resource.fileName}:${resource.localBlob.size}`;
  }

  const params = resource.params || {};
  const paramsKey = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join('&');

  return `${resource.endpoint || ''}|${paramsKey}|${resource.fileName || ''}`;
};

export const buildIndexedOwnerEntriesSnapshot = (entries, scannedPages, totalPages) => {
  return entries.map((entry, index) => ({
    ...entry,
    endPage: index < entries.length - 1
      ? entries[index].endPage
      : Math.max(entry.startPage, Math.min(totalPages, scannedPages))
  }));
};

const isExcelMimeType = (mimeType) => {
  const normalized = (mimeType || '').toString().trim().toLowerCase();
  return normalized.includes('excel')
    || normalized.endsWith('/xls')
    || normalized.endsWith('/xlsx')
    || normalized === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
};

const isEmlMimeType = (mimeType) => {
  const normalized = (mimeType || '').toString().trim().toLowerCase();
  return normalized === 'message/rfc822' || normalized.endsWith('/eml');
};

const isMsgMimeType = (mimeType) => {
  const normalized = (mimeType || '').toString().trim().toLowerCase();
  return normalized === 'application/vnd.ms-outlook' || normalized.endsWith('/msg');
};

const friendlyFileType = (fileType, fileName, fileExtension) => {
  const extension = normalizeFileExtension(fileExtension, fileName);
  if (extension && EXTENSION_LABELS[extension]) return EXTENSION_LABELS[extension];

  const normalized = resolveMimeType(fileType, fileName, fileExtension);
  if (normalized.startsWith('image/')) return 'Image';
  if (FILE_TYPE_LABELS[normalized]) return FILE_TYPE_LABELS[normalized];
  if (normalized.includes('excel') || normalized.endsWith('/xls') || normalized.endsWith('/xlsx')) return 'Excel';
  if (normalized.includes('word') || normalized.endsWith('/doc') || normalized.endsWith('/docx')) return 'Word';
  if (normalized.includes('pdf')) return 'PDF';
  if (normalized.includes('message/rfc822') || normalized.endsWith('/eml') || normalized.includes('email')) return 'Email';
  if (normalized.includes('outlook') || normalized.endsWith('/msg')) return 'Outlook Email';
  if (normalized.includes('zip')) return 'ZIP';

  return fileType || (extension ? extension.toUpperCase() : '-');
};

const canPreviewFile = (fileType, fileName, fileExtension) => {
  const extension = normalizeFileExtension(fileExtension, fileName);
  if (PREVIEWABLE_EXTENSIONS.has(extension)) return true;

  const mimeType = resolveMimeType(fileType, fileName, fileExtension);
  return mimeType === 'application/pdf'
    || mimeType.startsWith('image/')
    || isExcelMimeType(mimeType)
    || isEmlMimeType(mimeType)
    || isMsgMimeType(mimeType);
};

const getExcelSheetRows = (workbook, sheetName) => {
  const worksheet = workbook?.Sheets?.[sheetName];
  if (!worksheet) return [];

  return XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: true
  });
};

const getExcelColumnLabel = (columnIndex) => {
  let label = '';
  let current = columnIndex + 1;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    current = Math.floor((current - 1) / 26);
  }

  return label;
};

const getExcelHeaderCellText = (row, columnIndex) => {
  return String(Array.isArray(row) ? (row[columnIndex] ?? '') : '').trim();
};

const formatEmailAddressEntry = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry !== 'object') return '';
  const name = String(entry.name || '').trim();
  const address = String(entry.address || entry.email || entry.senderEmail || '').trim();
  if (name && address) return `${name} <${address}>`;
  return address || name || '';
};

const formatEmailAddressList = (entries) => {
  if (!Array.isArray(entries)) return '';
  return entries
    .map((entry) => formatEmailAddressEntry(entry))
    .filter(Boolean)
    .join(', ');
};

const getEmailPreviewBody = (email) => {
  const textBody = String(email?.text || '').trim();
  if (textBody) return textBody;

  const htmlBody = String(email?.html || email?.bodyHtml || '').trim();
  if (!htmlBody) return '';

  if (typeof DOMParser === 'function') {
    const parsed = new DOMParser().parseFromString(htmlBody, 'text/html');
    return parsed.body?.textContent?.replace(/\n{3,}/g, '\n\n').trim() || '';
  }

  return htmlBody
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatEmailDateValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return EMAIL_DATE_TIME_FORMATTER.format(parsed);
};

const decodeBase64ToUint8Array = (value) => {
  const normalized = String(value || '').replace(/\s+/g, '');
  if (!normalized) return new Uint8Array();

  if (typeof atob === 'function') {
    const decoded = atob(normalized);
    return Uint8Array.from(decoded, (char) => char.charCodeAt(0));
  }

  const globalBuffer = typeof window !== 'undefined' ? window.Buffer : undefined;
  if (globalBuffer?.from) {
    return Uint8Array.from(globalBuffer.from(normalized, 'base64'));
  }

  throw new Error('Base64 decoding is not available in this environment.');
};

const getBinaryContentBytes = (content, encoding = '') => {
  if (content instanceof Uint8Array) return content;
  if (content instanceof ArrayBuffer) return new Uint8Array(content);

  const normalizedEncoding = String(encoding || '').toLowerCase();
  const stringContent = typeof content === 'string' ? content : '';

  if (!stringContent) return new Uint8Array();
  if (normalizedEncoding === 'base64') return decodeBase64ToUint8Array(stringContent);

  return new TextEncoder().encode(stringContent);
};

const buildLocalAttachmentDescriptor = ({
  baseKey,
  fileName,
  fileType,
  fileExtension,
  content,
  encoding,
  backLabel,
  errorSetter,
  viewerContextType = '',
  viewerContextDescription = ''
}) => {
  const resolvedFileName = getResolvedFileName(fileName, fileExtension, `${baseKey}-attachment`);
  const normalizedExtension = normalizeFileExtension(fileExtension, resolvedFileName);
  const mimeType = resolveMimeType(fileType, resolvedFileName, normalizedExtension);
  const bytes = getBinaryContentBytes(content, encoding);
  const localBlob = new Blob([bytes], { type: mimeType || 'application/octet-stream' });

  return {
    key: `${baseKey}-${resolvedFileName}-${normalizedExtension || 'file'}`,
    fileName: resolvedFileName,
    fileType,
    mimeType,
    fileExtension: normalizedExtension,
    fileTypeLabel: friendlyFileType(fileType, resolvedFileName, normalizedExtension),
    canPreview: canPreviewFile(fileType, resolvedFileName, normalizedExtension),
    isImage: mimeType.startsWith('image/'),
    isPdf: mimeType === 'application/pdf',
    backLabel,
    errorSetter,
    viewerContextType,
    viewerContextDescription,
    localBlob
  };
};

const compareExcelCellValues = (leftValue, rightValue) => {
  const leftText = String(leftValue ?? '').trim();
  const rightText = String(rightValue ?? '').trim();
  const leftIsBlank = !leftText;
  const rightIsBlank = !rightText;

  if (leftIsBlank && rightIsBlank) return 0;
  if (leftIsBlank) return 1;
  if (rightIsBlank) return -1;

  const normalizedLeftNumber = Number(leftText.replace(/,/g, ''));
  const normalizedRightNumber = Number(rightText.replace(/,/g, ''));
  const leftIsNumber = Number.isFinite(normalizedLeftNumber);
  const rightIsNumber = Number.isFinite(normalizedRightNumber);

  if (leftIsNumber && rightIsNumber) {
    if (normalizedLeftNumber === normalizedRightNumber) return 0;
    return normalizedLeftNumber < normalizedRightNumber ? -1 : 1;
  }

  return leftText.localeCompare(rightText, undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

const formatCalendarDate = (value) => {
  if (!value) return '-';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return DATE_FORMATTER.format(new Date(Number(year), Number(month) - 1, Number(day)));
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : DATE_FORMATTER.format(parsed);
};

const formatDateOnly = (value) => {
  if (!value) return '-';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${month}/${day}/${year}`;
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  const year = parsed.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

const toSortableDateValue = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return Date.UTC(Number(year), Number(month) - 1, Number(day));
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const CaoPage = ({ onLogout }) => {
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [restricted, setRestricted] = useState(false); // Contracts access for any user
  const [chiefCountyId, setChiefCountyId] = useState(null);
  const [contractsCountyId, setContractsCountyId] = useState(null);
  const [counties, setCounties] = useState([]);

  const [year, setYear] = useState(DEFAULT_YEAR);
  const [typeId, setTypeId] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [contractsTypeId, setContractsTypeId] = useState(null);
  const [contractsTypeOptions, setContractsTypeOptions] = useState([]);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [contractsRecords, setContractsRecords] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState('');

  // Protest tracking
  const [protestCountyId, setProtestCountyId] = useState(null);
  const [protestYear, setProtestYear] = useState(DEFAULT_YEAR);
  const [protestTypeId, setProtestTypeId] = useState(null);
  const [protestTypeOptions, setProtestTypeOptions] = useState([]);
  const [protestStatusOptions, setProtestStatusOptions] = useState([]);
  const [protestClientStatusOptions, setProtestClientStatusOptions] = useState([]);
  const [protestStatusCode, setProtestStatusCode] = useState('');
  const [protestClientStatusCode, setProtestClientStatusCode] = useState('');
  const [assignedOptions, setAssignedOptions] = useState([]);
  const [assignedName, setAssignedName] = useState('');
  const [protestRecords, setProtestRecords] = useState([]);
  const [protestLoading, setProtestLoading] = useState(false);
  const [protestError, setProtestError] = useState('');
  const [protestCollapsed, setProtestCollapsed] = useState(false);
  const [protestSortConfig, setProtestSortConfig] = useState({ column: null, direction: 'asc' });
  const [protestAttachments, setProtestAttachments] = useState([]);
  const [protestAttachmentsLoading, setProtestAttachmentsLoading] = useState(false);
  const [protestAttachmentsError, setProtestAttachmentsError] = useState('');
  const [showProtestAttachmentsModal, setShowProtestAttachmentsModal] = useState(false);
  const [selectedProtestRow, setSelectedProtestRow] = useState(null);
  const [pendingFileAction, setPendingFileAction] = useState({ key: '', mode: '' });
  const [sharedDocumentViewer, setSharedDocumentViewer] = useState(null);
  const [documentViewer, setDocumentViewer] = useState(createEmptyViewerState);
  const [documentViewerPageCount, setDocumentViewerPageCount] = useState(0);
  const [documentViewerCurrentPage, setDocumentViewerCurrentPage] = useState(1);
  const [documentViewerDisplayedPdfPage, setDocumentViewerDisplayedPdfPage] = useState(1);
  const [documentViewerPageInput, setDocumentViewerPageInput] = useState('1');
  const [documentViewerError, setDocumentViewerError] = useState('');
  const [documentViewerLoading, setDocumentViewerLoading] = useState(false);
  const [documentViewerSheetNames, setDocumentViewerSheetNames] = useState([]);
  const [documentViewerActiveSheetName, setDocumentViewerActiveSheetName] = useState('');
  const [documentViewerExcelRows, setDocumentViewerExcelRows] = useState([]);
  const [documentViewerExcelFilters, setDocumentViewerExcelFilters] = useState({});
  const [documentViewerExcelSortConfig, setDocumentViewerExcelSortConfig] = useState({ column: null, direction: 'asc' });
  const [documentViewerEmailPreview, setDocumentViewerEmailPreview] = useState(null);
  const [documentViewerPdfOwnerIndexState, setDocumentViewerPdfOwnerIndexState] = useState(createEmptyPdfOwnerIndexState);
  const [documentViewerPdfOwnerQuery, setDocumentViewerPdfOwnerQuery] = useState('');
  const [documentViewerPdfOwnerDownloadKey, setDocumentViewerPdfOwnerDownloadKey] = useState('');
  const [documentViewerPdfOwnerDownloadError, setDocumentViewerPdfOwnerDownloadError] = useState('');
  const [documentViewerPdfRangeStartInput, setDocumentViewerPdfRangeStartInput] = useState('1');
  const [documentViewerPdfRangeEndInput, setDocumentViewerPdfRangeEndInput] = useState('1');
  const [documentViewerPdfRangeActionMode, setDocumentViewerPdfRangeActionMode] = useState('');
  const [documentViewerPdfRangeError, setDocumentViewerPdfRangeError] = useState('');
  const [documentViewerPdfPageLoading, setDocumentViewerPdfPageLoading] = useState(false);
  const [documentViewerWidth, setDocumentViewerWidth] = useState(0);
  const [documentViewerScale, setDocumentViewerScale] = useState(getDefaultDocumentViewerScale);
  const [documentViewerZoomInput, setDocumentViewerZoomInput] = useState(
    () => String(getDefaultDocumentViewerScale() * 100)
  );
  const [documentViewerRotation, setDocumentViewerRotation] = useState(0);
  const [documentViewerAppearance, setDocumentViewerAppearance] = useState('light');
  const documentViewerBodyRef = useRef(null);
  const documentViewerPdfRef = useRef(null);
  const documentViewerPageRefs = useRef([]);
  const documentViewerPdfRenderStateRef = useRef({ pageNumber: 0, canvasReady: false, textLayerReady: false });
  const documentViewerPdfRevealAnimationFrameRef = useRef(0);
  const documentViewerWorkbookRef = useRef(null);
  const documentViewerLoadTokenRef = useRef(0);
  const documentViewerExcelWrapRef = useRef(null);
  const documentViewerLocalUrlRef = useRef('');
  const documentViewerHistoryRef = useRef([]);

  // Collapsible UI
  const [chiefCollapsed, setChiefCollapsed] = useState(false);
  const [contractsCollapsed, setContractsCollapsed] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const sess = await apiClient.get('/auth/session', { withCredentials: true });
        const admin = !!sess.data?.isAdmin;
        setUsername(sess.data?.username || '');
        setIsAdmin(admin);
        setRestricted(Boolean(sess.data?.restricted)); // flag controls Contracts visibility
        const countiesRes = await apiClient.get('/lookup/counties', { withCredentials: true });
        setCounties(countiesRes.data || []);

        if (!admin) {
          // support both countyID and CountyID shapes from session
          const sessionCounty = sess.data?.countyID ?? sess.data?.CountyID ?? null;
          setChiefCountyId(sessionCounty);
          setContractsCountyId(sessionCounty);
          setProtestCountyId(sessionCounty);
        }
      } catch (err) {
        console.error('Failed to load session for CAO page:', err);
      }
    };

    loadSession();
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await apiClient.get(`/lookup/types/${CHIEF_CATEGORY_ID}`, { withCredentials: true });
        const opts = Array.isArray(res.data) ? res.data : [];
        setTypeOptions(opts);
        if (opts.length) setTypeId(opts[0].ID ?? opts[0].id ?? null);
      } catch (err) {
        console.error('Failed to load types for Chief category:', err);
        setTypeOptions([]);
        setTypeId(null);
      }
    };
    fetchTypes();
  }, []);

  // Load types for Contracts category when allowed
  useEffect(() => {
    if (!restricted) return;
    const fetchTypes = async () => {
      try {
        const res = await apiClient.get(`/lookup/types/${CONTRACTS_CATEGORY_ID}`, { withCredentials: true });
        const opts = Array.isArray(res.data) ? res.data : [];
        setContractsTypeOptions(opts);
        if (opts.length) setContractsTypeId(opts[0].ID ?? opts[0].id ?? null);
      } catch (err) {
        console.error('Failed to load types for Contracts category:', err);
        setContractsTypeOptions([]);
        setContractsTypeId(null);
      }
    };
    fetchTypes();
  }, [restricted, isAdmin]);

  // Protest lookups
  useEffect(() => {
    const fetchProtestTypes = async () => {
      try {
        const res = await apiClient.get('/lookup/protest-types', { withCredentials: true });
        const opts = Array.isArray(res.data) ? res.data : [];
        setProtestTypeOptions(opts);
      } catch (err) {
        console.error('Failed to load protest types:', err);
        setProtestTypeOptions([]);
        setProtestTypeId(null);
      }
    };
    fetchProtestTypes();
  }, []);

  useEffect(() => {
    const fetchProtestStatuses = async () => {
      try {
        const res = await apiClient.get('/lookup/protest-statuses', { withCredentials: true });
        const rows = Array.isArray(res.data) ? res.data : [];

        const statusMap = new Map();
        const clientMap = new Map();
        const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
        const isEnabledFlag = (value) => value === true || value === 1 || value === '1';

        rows.forEach((row) => {
          const code = row.Code ?? row.StatusCode ?? row.code;
          const description = row.Description ?? row.StatusDescription ?? row.description;
          const clientDesc = row.ClientDescription ?? row.clientDescription ?? row.ClientStatus ?? null;
          const internalStatus = row.InternalStatus ?? row.internalStatus ?? 0;
          const externalStatus = row.ExternalStatus ?? row.externalStatus ?? 0;
          const hasInternalFlag = row.InternalStatus !== undefined || row.internalStatus !== undefined;
          const hasExternalFlag = row.ExternalStatus !== undefined || row.externalStatus !== undefined;

          if ((!hasInternalFlag || isEnabledFlag(internalStatus)) && hasValue(code) && hasValue(description) && !statusMap.has(code)) {
            statusMap.set(code, description);
          }

          if ((!hasExternalFlag || isEnabledFlag(externalStatus)) && hasValue(code) && hasValue(clientDesc)) {
            const key = `${code}::${clientDesc}`;
            if (!clientMap.has(key)) clientMap.set(key, { code, label: clientDesc });
          }
        });

        setProtestStatusOptions(Array.from(statusMap, ([code, label]) => ({ code, label })));
        setProtestClientStatusOptions(Array.from(clientMap.values()));
      } catch (err) {
        console.error('Failed to load protest statuses:', err);
        setProtestStatusOptions([]);
        setProtestClientStatusOptions([]);
      }
    };
    fetchProtestStatuses();
  }, []);

  const canFetch = useMemo(() => {
    const hasCounty = isAdmin ? !!chiefCountyId : chiefCountyId !== null && chiefCountyId !== undefined;
    return hasCounty && !!year && !!typeId;
  }, [isAdmin, chiefCountyId, year, typeId]);

  const canFetchContracts = useMemo(() => {
    if (!restricted) return false;
    const hasCounty = isAdmin ? !!contractsCountyId : contractsCountyId !== null && contractsCountyId !== undefined;
    return hasCounty && !!year && !!contractsTypeId;
  }, [restricted, isAdmin, contractsCountyId, year, contractsTypeId]);

  const canFetchProtests = useMemo(() => {
    const hasCounty = isAdmin ? !!protestCountyId : protestCountyId !== null && protestCountyId !== undefined;
    return hasCounty && !!protestYear;
  }, [isAdmin, protestCountyId, protestYear]);

  const visibleProtestTypeOptions = useMemo(() => {
    if (isAdmin) return protestTypeOptions;
    return protestTypeOptions.filter((t) => {
      const optionId = t.Id ?? t.ID ?? t.id;
      return !HIDDEN_NON_ADMIN_PROTEST_TYPE_IDS.has(Number(optionId));
    });
  }, [isAdmin, protestTypeOptions]);

  useEffect(() => {
    if (!isAdmin && protestStatusCode) {
      setProtestStatusCode('');
    }
  }, [isAdmin, protestStatusCode]);

  useEffect(() => {
    if (!isAdmin && protestTypeId && HIDDEN_NON_ADMIN_PROTEST_TYPE_IDS.has(Number(protestTypeId))) {
      setProtestTypeId(null);
    }
  }, [isAdmin, protestTypeId]);

  const sortedProtestRecords = useMemo(() => {
    if (!protestSortConfig.column) return protestRecords;

    const directionMultiplier = protestSortConfig.direction === 'asc' ? 1 : -1;
    const isDateColumn = protestSortConfig.column === 'ReceivedByTYP' || protestSortConfig.column === 'HearingDate';
    const isNumericColumn = protestSortConfig.column === 'AttachmentCount';

    const getSortValue = (row) => {
      switch (protestSortConfig.column) {
        case 'ReceivedByTYP':
          return toSortableDateValue(row.ReceivedByTYP);
        case 'OwnerName':
          return row.OwnerName || '';
        case 'ProtestTypeDescription':
          return row.ProtestTypeDescription
            || protestTypeOptions.find((t) => (t.Id ?? t.ID ?? t.id) === row.ProtestTypeID)?.Description
            || protestTypeOptions.find((t) => (t.Id ?? t.ID ?? t.id) === row.ProtestTypeID)?.Name
            || '';
        case 'HearingDate':
          return toSortableDateValue(row.HearingDate);
        case 'Status':
          return row.Status
            || protestStatusOptions.find((s) => s.code === (row.StatusCode || row.Status))?.label
            || '';
        case 'ClientStatus':
          return row.ClientStatus
            || protestClientStatusOptions.find((s) => s.code === (row.ClientStatusCode || row.ClientStatus))?.label
            || '';
        case 'AssignedTo':
          return row.AssignedToFullName || row.AssignedFullName || row.AssignedTo || '';
        case 'AttachmentCount':
          return Number(row.AttachmentCount ?? 0);
        default:
          return '';
      }
    };

    return [...protestRecords].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      const aMissing = aValue === null || aValue === undefined || aValue === '';
      const bMissing = bValue === null || bValue === undefined || bValue === '';

      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;

      if (isDateColumn) {
        if (aValue === bValue) return 0;
        return aValue > bValue ? directionMultiplier : -directionMultiplier;
      }

      if (isNumericColumn) {
        if (aValue === bValue) return 0;
        return aValue > bValue ? directionMultiplier : -directionMultiplier;
      }

      const comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
      return comparison * directionMultiplier;
    });
  }, [protestRecords, protestSortConfig, protestTypeOptions, protestStatusOptions, protestClientStatusOptions]);

  const fetchRecords = async () => {
    if (!canFetch) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/owners/owner-attachments/cao-documents', {
        params: {
          countyId: chiefCountyId,
          appraisalYear: year,
          categoryId: CHIEF_CATEGORY_ID,
          typeId
        },
        withCredentials: true
      });
      setRecords(Array.isArray(res.data) ? res.data : res.data?.records || []);
    } catch (err) {
      console.error('Failed to fetch CAO documents:', err);
      setError('Unable to load CAO documents.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto fetch when all required filters are set
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, chiefCountyId, year, typeId]);

  const fetchContractsRecords = async () => {
    if (!canFetchContracts) return;
    setContractsLoading(true);
    setContractsError('');
    try {
      const res = await apiClient.get('/owners/owner-attachments/cao-documents', {
        params: {
          countyId: contractsCountyId,
          appraisalYear: year,
          categoryId: CONTRACTS_CATEGORY_ID,
          typeId: contractsTypeId
        },
        withCredentials: true
      });
      setContractsRecords(Array.isArray(res.data) ? res.data : res.data?.records || []);
    } catch (err) {
      console.error('Failed to fetch Contracts documents:', err);
      setContractsError('Unable to load Contracts documents.');
      setContractsRecords([]);
    } finally {
      setContractsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractsRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetchContracts, contractsCountyId, year, contractsTypeId]);

  const dedupeNames = (names) => {
    const seen = new Set();
    return names
      .filter((n) => {
        if (!n) return false;
        const trimmed = n.toString().trim();
        if (!trimmed || seen.has(trimmed)) return false;
        seen.add(trimmed);
        return true;
      })
      .sort((a, b) => a.localeCompare(b));
  };

  const fetchAssignees = async () => {
    if (!canFetchProtests) return;
    try {
      const res = await apiClient.get('/owners/protests/track', {
        params: {
          countyId: protestCountyId,
          appraisalYear: protestYear
        },
        withCredentials: true
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.records || [];
      const names = dedupeNames(rows.map((r) => r.AssignedToFullName || r.AssignedFullName || r.AssignedTo || r.Assignee || r.FullName));
      setAssignedOptions(names);
      if (assignedName && !names.includes(assignedName)) setAssignedName('');
    } catch (err) {
      console.error('Failed to fetch assignees for protest tracker:', err);
      setAssignedOptions([]);
    }
  };

  useEffect(() => {
    fetchAssignees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protestCountyId, protestYear, canFetchProtests]);

  const fetchProtestRecords = async () => {
    if (!canFetchProtests) return;
    setProtestLoading(true);
    setProtestError('');
    try {
      const res = await apiClient.get('/owners/protests/track', {
        params: {
          countyId: protestCountyId,
          appraisalYear: protestYear,
          protestTypeId: protestTypeId || null,
          statusCode: protestStatusCode || null,
          clientStatusCode: protestClientStatusCode || null,
          assignedFullName: assignedName || null
        },
        withCredentials: true
      });
      setProtestRecords(Array.isArray(res.data) ? res.data : res.data?.records || []);
    } catch (err) {
      console.error('Failed to fetch protest tracking data:', err);
      setProtestError('Unable to load protest records.');
      setProtestRecords([]);
    } finally {
      setProtestLoading(false);
    }
  };

  useEffect(() => {
    fetchProtestRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetchProtests, protestCountyId, protestYear, protestTypeId, protestStatusCode, protestClientStatusCode, assignedName]);

  const renderTypeName = (id) => {
    const match = typeOptions.find((t) => (t.ID ?? t.id) === id);
    return match?.Name || match?.name || id;
  };

  const renderContractsTypeName = (id) => {
    const match = contractsTypeOptions.find((t) => (t.ID ?? t.id) === id);
    return match?.Name || match?.name || id;
  };

  const renderProtestTypeName = (id) => {
    const match = protestTypeOptions.find((t) => (t.Id ?? t.ID ?? t.id) === id);
    return match?.Description || match?.Name || match?.name || id || '-';
  };

  const renderStatusLabel = (value) => {
    if (!value) return '-';
    const match = protestStatusOptions.find((s) => s.code === value);
    return match?.label || value;
  };

  const renderClientStatusLabel = (value) => {
    if (!value) return '-';
    const match = protestClientStatusOptions.find((s) => s.code === value);
    return match?.label || value;
  };

  const renderCountyName = (sigmaGeoNbr) => {
    if (!sigmaGeoNbr) return '-';
    const match = counties.find((c) => {
      const countyId = c.CountyID ?? c.id ?? c.Id;
      return countyId === Number(sigmaGeoNbr) || String(countyId) === String(sigmaGeoNbr);
    });
    return match?.Name || match?.CountyName || sigmaGeoNbr;
  };

  const shouldShowChief = !isAdmin || !!chiefCountyId;
  const protestStatusLabel = 'TYP Status';
  const protestClientStatusLabel = isAdmin ? 'Client Status' : 'Status';
  const isPdfViewerOpen = documentViewer.open && documentViewer.mimeType === 'application/pdf';
  const isExcelViewerOpen = documentViewer.open && isExcelMimeType(documentViewer.mimeType);
  const isEmlViewerOpen = documentViewer.open && isEmlMimeType(documentViewer.mimeType);
  const isMsgViewerOpen = documentViewer.open && isMsgMimeType(documentViewer.mimeType);
  const isEmailViewerOpen = isEmlViewerOpen || isMsgViewerOpen;
  const isLargePdfViewerOpen = isPdfViewerOpen
    && Boolean(documentViewer.resource?.ownerIndexedPdfEligible)
    && documentViewerPageCount > LARGE_PDF_PAGE_THRESHOLD;
  const isPdfViewerReadyToRender = isPdfViewerOpen && documentViewerPageCount > 0 && documentViewerWidth > 0;
  const shouldShowPdfTools = isPdfViewerOpen && documentViewerPageCount > 0;
  const documentViewerRenderedPdfPageCount = isPdfViewerReadyToRender ? 1 : 0;
  const shouldShowPdfLoader = isPdfViewerOpen
    && !documentViewerError
    && (
      !isPdfViewerReadyToRender
      || documentViewerLoading
      || (isLargePdfViewerOpen && documentViewerPdfPageLoading)
    );
  const isDocumentViewerPdfNavigationLocked = shouldShowPdfLoader || documentViewerPdfPageLoading;
  const documentViewerPdfLoaderMessage = documentViewerPageCount > 0
    ? `Loading page ${Math.min(documentViewerCurrentPage, documentViewerPageCount).toLocaleString()} of ${documentViewerPageCount.toLocaleString()}...`
    : 'Loading PDF...';
  const shouldShowDocumentViewerContext = !!selectedProtestRow && (
    isPdfViewerOpen || isExcelViewerOpen || isEmailViewerOpen
  );
  const documentViewerOwnerName = selectedProtestRow?.OwnerName
    || (selectedProtestRow?.ProtestID ? `Protest ${selectedProtestRow.ProtestID}` : '-');
  const documentViewerProtestType = selectedProtestRow
    ? (selectedProtestRow.ProtestTypeDescription || renderProtestTypeName(selectedProtestRow.ProtestTypeID))
    : '-';
  const documentViewerAttachmentType = String(documentViewer.resource?.viewerContextType || '').trim();
  const documentViewerAttachmentDescription = String(documentViewer.resource?.viewerContextDescription || '').trim();
  const documentViewerExcelColumnCount = useMemo(
    () => documentViewerExcelRows.reduce(
      (maxColumns, row) => Math.max(maxColumns, Array.isArray(row) ? row.length : 0),
      0
    ),
    [documentViewerExcelRows]
  );
  const documentViewerExcelHeaderRow = useMemo(
    () => (documentViewerExcelRows.length > 0 && Array.isArray(documentViewerExcelRows[0]))
      ? documentViewerExcelRows[0]
      : [],
    [documentViewerExcelRows]
  );
  const documentViewerExcelBodyRows = useMemo(
    () => documentViewerExcelRows.slice(1).map((row, rowIndex) => ({
      sheetRowNumber: rowIndex + 2,
      row: Array.isArray(row) ? row : []
    })),
    [documentViewerExcelRows]
  );
  const documentViewerExcelActiveFilters = useMemo(
    () => Object.entries(documentViewerExcelFilters)
      .map(([columnIndex, value]) => [Number(columnIndex), String(value || '').trim().toLowerCase()])
      .filter(([, value]) => value),
    [documentViewerExcelFilters]
  );
  const documentViewerFilteredExcelRows = useMemo(() => {
    if (!documentViewerExcelActiveFilters.length) return documentViewerExcelBodyRows;

    return documentViewerExcelBodyRows.filter(({ row }) => {
      return documentViewerExcelActiveFilters.every(([columnIndex, filterValue]) => {
        const cellValue = row[columnIndex];
        return String(cellValue ?? '').toLowerCase().includes(filterValue);
      });
    });
  }, [documentViewerExcelBodyRows, documentViewerExcelActiveFilters]);
  const documentViewerSortedExcelRows = useMemo(() => {
    const rows = documentViewerFilteredExcelRows;

    if (documentViewerExcelSortConfig.column === null) return rows;

    const directionMultiplier = documentViewerExcelSortConfig.direction === 'desc' ? -1 : 1;
    const columnIndex = documentViewerExcelSortConfig.column;

    return [...rows].sort((left, right) => {
      const comparison = compareExcelCellValues(left.row[columnIndex], right.row[columnIndex]);
      if (comparison !== 0) return comparison * directionMultiplier;
      return left.sheetRowNumber - right.sheetRowNumber;
    });
  }, [documentViewerFilteredExcelRows, documentViewerExcelSortConfig]);
  const pdfViewerFile = useMemo(() => {
    if (!isPdfViewerOpen || !documentViewer.previewUrl) return null;
    return {
      url: documentViewer.previewUrl,
      withCredentials: true
    };
  }, [documentViewer.previewUrl, isPdfViewerOpen]);
  const documentViewerPdfOwnerResults = useMemo(() => {
    const normalizedQuery = normalizeViewerSearchValue(documentViewerPdfOwnerQuery);
    if (!normalizedQuery) return [];

    return documentViewerPdfOwnerIndexState.entries
      .filter((entry) => entry.searchText.includes(normalizedQuery))
      .slice(0, PDF_OWNER_SEARCH_RESULT_LIMIT);
  }, [documentViewerPdfOwnerIndexState.entries, documentViewerPdfOwnerQuery]);
  const canDownloadOwnerPdfRanges = documentViewerPdfOwnerIndexState.status === 'ready' && Boolean(documentViewer.resource);

  const handleProtestSort = (column) => {
    setProtestSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const renderProtestSortableHeader = (columnKey, label) => {
    const isActive = protestSortConfig.column === columnKey;
    const isAscending = isActive && protestSortConfig.direction === 'asc';
    const isDescending = isActive && protestSortConfig.direction === 'desc';
    const ariaSort = isActive ? (isAscending ? 'ascending' : 'descending') : 'none';

    return (
      <th aria-sort={ariaSort} className="cao-sortable-th">
          <button
            type="button"
            className={'cao-sort-header-button' + (isActive ? ' active' : '')}
            onClick={() => handleProtestSort(columnKey)}
            aria-label={`Sort ${label}`}
          >
            <span>{label}</span>
            <span className={'sort-arrow-group cao-sort-arrow-group' + (isActive ? ' active' : '')}>
            <span className={'sort-arrow cao-sort-arrow sort-arrow--up' + (isAscending ? ' active' : '')}>{UP_ARROW}</span>
            <span className={'sort-arrow cao-sort-arrow sort-arrow--down' + (isDescending ? ' active' : '')}>{DOWN_ARROW}</span>
            </span>
          </button>
      </th>
    );
  };

  const setDescriptorError = (descriptor, message) => {
    descriptor?.errorSetter?.(message);
  };

  const clearDescriptorError = (descriptor) => {
    descriptor?.errorSetter?.('');
  };

  const cancelDocumentViewerPdfRevealAnimationFrame = () => {
    if (!documentViewerPdfRevealAnimationFrameRef.current || typeof window === 'undefined') return;
    window.cancelAnimationFrame(documentViewerPdfRevealAnimationFrameRef.current);
    documentViewerPdfRevealAnimationFrameRef.current = 0;
  };

  const revokeDocumentViewerLocalUrl = () => {
    if (!documentViewerLocalUrlRef.current) return;
    URL.revokeObjectURL(documentViewerLocalUrlRef.current);
    documentViewerLocalUrlRef.current = '';
  };

  const restoreDocumentViewerSnapshot = (snapshot) => {
    cancelDocumentViewerPdfRevealAnimationFrame();
    setDocumentViewerPageCount(0);
    setDocumentViewerCurrentPage(1);
    setDocumentViewerDisplayedPdfPage(1);
    setDocumentViewerPageInput('1');
    setDocumentViewerError('');
    setDocumentViewerLoading(false);
    setDocumentViewerPdfPageLoading(false);
    setDocumentViewerSheetNames([]);
    setDocumentViewerActiveSheetName('');
    setDocumentViewerExcelRows([]);
    setDocumentViewerExcelFilters({});
    setDocumentViewerExcelSortConfig({ column: null, direction: 'asc' });
    setDocumentViewerEmailPreview(snapshot?.emailPreview || null);
    setDocumentViewerPdfOwnerIndexState(createEmptyPdfOwnerIndexState());
    setDocumentViewerPdfOwnerQuery('');
    setDocumentViewerPdfOwnerDownloadKey('');
    setDocumentViewerPdfOwnerDownloadError('');
    setDocumentViewerPdfRangeStartInput('1');
    setDocumentViewerPdfRangeEndInput('1');
    setDocumentViewerPdfRangeActionMode('');
    setDocumentViewerPdfRangeError('');
    setDocumentViewerWidth(0);
    setDocumentViewerScale(getDefaultDocumentViewerScale());
    setDocumentViewerRotation(0);
    documentViewerPdfRef.current = null;
    documentViewerPageRefs.current = [];
    documentViewerPdfRenderStateRef.current = { pageNumber: 0, canvasReady: false, textLayerReady: false };
    documentViewerWorkbookRef.current = null;
    setDocumentViewer(snapshot?.viewer || createEmptyViewerState());
  };

  const closeDocumentViewer = () => {
    documentViewerLoadTokenRef.current += 1;
    cancelDocumentViewerPdfRevealAnimationFrame();
    revokeDocumentViewerLocalUrl();

    const previousSnapshot = documentViewerHistoryRef.current.pop();
    if (previousSnapshot) {
      restoreDocumentViewerSnapshot(previousSnapshot);
      return;
    }

    documentViewerHistoryRef.current = [];
    setDocumentViewerPageCount(0);
    setDocumentViewerCurrentPage(1);
    setDocumentViewerDisplayedPdfPage(1);
    setDocumentViewerPageInput('1');
    setDocumentViewerError('');
    setDocumentViewerLoading(false);
    setDocumentViewerPdfPageLoading(false);
    setDocumentViewerSheetNames([]);
    setDocumentViewerActiveSheetName('');
    setDocumentViewerExcelRows([]);
    setDocumentViewerExcelFilters({});
    setDocumentViewerExcelSortConfig({ column: null, direction: 'asc' });
    setDocumentViewerEmailPreview(null);
    setDocumentViewerPdfOwnerIndexState(createEmptyPdfOwnerIndexState());
    setDocumentViewerPdfOwnerQuery('');
    setDocumentViewerPdfOwnerDownloadKey('');
    setDocumentViewerPdfOwnerDownloadError('');
    setDocumentViewerPdfRangeStartInput('1');
    setDocumentViewerPdfRangeEndInput('1');
    setDocumentViewerPdfRangeActionMode('');
    setDocumentViewerPdfRangeError('');
    setDocumentViewerWidth(0);
    setDocumentViewerScale(getDefaultDocumentViewerScale());
    setDocumentViewerRotation(0);
    documentViewerPdfRef.current = null;
    documentViewerPageRefs.current = [];
    documentViewerPdfRenderStateRef.current = { pageNumber: 0, canvasReady: false, textLayerReady: false };
    documentViewerWorkbookRef.current = null;
    setDocumentViewer(createEmptyViewerState());
  };

  const buildDescriptorUrl = (descriptor, extraParams = {}) => {
    const url = new URL(buildApiUrl(descriptor.endpoint));
    const params = { ...descriptor.params, ...extraParams };

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, String(value));
    });

    return url.toString();
  };

  const triggerBlobDownload = (blob, fileName) => {
    if (!(blob instanceof Blob)) return;

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const triggerDirectDownload = (descriptor) => {
    if (descriptor?.localBlob instanceof Blob) {
      triggerBlobDownload(descriptor.localBlob, descriptor.fileName);
      return;
    }

    const link = document.createElement('a');
    link.href = buildDescriptorUrl(descriptor, { download: 1 });
    link.download = descriptor.fileName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openDocumentViewerPdfInNewTab = () => {
    if (!isPdfViewerOpen || !documentViewer.resource) return;

    const shouldUseLoadedPreview = documentViewer.resource.localBlob instanceof Blob;
    const pdfUrl = shouldUseLoadedPreview
      ? documentViewer.previewUrl
      : buildDescriptorUrl(documentViewer.resource);

    if (!pdfUrl) return;

    const newWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      console.warn('The browser blocked the PDF from opening in a new tab.');
    }
  };

  const loadDescriptorArrayBuffer = async (descriptor) => {
    if (descriptor?.localBlob instanceof Blob) {
      return descriptor.localBlob.arrayBuffer();
    }

    const response = await apiClient.get(descriptor.endpoint, {
      params: descriptor.params,
      responseType: 'arraybuffer'
    });

    return response.data;
  };

  const buildDocumentViewerPdfRangeBlob = async (descriptor, startValue, endValue) => {
    if (!descriptor) {
      throw new Error('No PDF file is available.');
    }

    const sourceBuffer = await loadDescriptorArrayBuffer(descriptor);
    const sourcePdf = await PDFDocument.load(sourceBuffer);
    const sourcePageCount = sourcePdf.getPageCount();
    const rawStartPage = Number(startValue);
    const rawEndPage = Number(endValue);

    if (!Number.isInteger(rawStartPage) || !Number.isInteger(rawEndPage)) {
      throw new Error('Enter valid page numbers.');
    }

    if (rawStartPage < 1 || rawEndPage < 1) {
      throw new Error('Page numbers must be 1 or greater.');
    }

    const startPage = Math.min(sourcePageCount, rawStartPage);
    const endPage = Math.min(sourcePageCount, rawEndPage);
    if (endPage < startPage) {
      throw new Error('To page must be greater than or equal to From page.');
    }

    const pageIndexes = Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage - 1 + index
    );

    if (!pageIndexes.length) {
      throw new Error('No pages were resolved for the selected range.');
    }

    const outputPdf = await PDFDocument.create();
    const copiedPages = await outputPdf.copyPages(sourcePdf, pageIndexes);
    copiedPages.forEach((page) => outputPdf.addPage(page));

    const outputBytes = await outputPdf.save();
    return {
      startPage,
      endPage,
      sourcePageCount,
      blob: new Blob([outputBytes], { type: 'application/pdf' })
    };
  };

  const printPdfBlob = (blob) => new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) {
      reject(new Error('Print preview is not available.'));
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    let settled = false;

    const cleanup = () => {
      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        iframe.remove();
      }, 1000);
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.setAttribute('aria-hidden', 'true');

    iframe.onload = () => {
      try {
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) {
          fail(new Error('Print preview is not available.'));
          return;
        }

        const handleAfterPrint = () => {
          try {
            frameWindow.removeEventListener('afterprint', handleAfterPrint);
          } catch (_err) {
            // Ignore cleanup failures from embedded PDF viewers.
          }
          finish();
        };

        try {
          frameWindow.addEventListener('afterprint', handleAfterPrint);
        } catch (_err) {
          // Some embedded PDF viewers do not expose afterprint.
        }

        frameWindow.focus();
        frameWindow.print();
        window.setTimeout(finish, 2000);
      } catch (error) {
        fail(error);
      }
    };

    iframe.onerror = () => fail(new Error('Unable to open print preview.'));
    iframe.src = objectUrl;
    document.body.appendChild(iframe);
  });

  const loadExcelWorkbook = async (descriptor, loadToken) => {
    const workbookBuffer = await loadDescriptorArrayBuffer(descriptor);
    if (workbookBuffer.byteLength >= MAX_EXCEL_PREVIEW_BYTES) {
      throw new Error('This Excel workbook is too large to preview safely. Use Download to open it in Excel.');
    }
    const workbook = XLSX.read(workbookBuffer, { type: 'array' });
    const sheetNames = Array.isArray(workbook.SheetNames) ? workbook.SheetNames : [];
    if (!sheetNames.length) {
      throw new Error('No worksheets found in workbook.');
    }

    if (documentViewerLoadTokenRef.current !== loadToken) return;

    const firstSheetName = sheetNames[0];
    documentViewerWorkbookRef.current = workbook;
    setDocumentViewerSheetNames(sheetNames);
    setDocumentViewerActiveSheetName(firstSheetName);
    setDocumentViewerExcelRows(getExcelSheetRows(workbook, firstSheetName));
    setDocumentViewerError('');
  };

  const loadEmlPreview = async (descriptor, loadToken) => {
    const emailBuffer = await loadDescriptorArrayBuffer(descriptor);
    const parsedEmail = await PostalMime.parse(emailBuffer);
    if (documentViewerLoadTokenRef.current !== loadToken) return;

    const previewBody = getEmailPreviewBody(parsedEmail);
    setDocumentViewerEmailPreview({
      previewType: 'eml',
      subject: String(parsedEmail?.subject || '').trim(),
      from: formatEmailAddressEntry(parsedEmail?.from),
      to: formatEmailAddressList(parsedEmail?.to),
      cc: formatEmailAddressList(parsedEmail?.cc),
      bcc: formatEmailAddressList(parsedEmail?.bcc),
      date: formatEmailDateValue(parsedEmail?.date),
      body: previewBody,
      attachments: Array.isArray(parsedEmail?.attachments)
        ? parsedEmail.attachments.map((attachment, index) => {
          const attachmentDescriptor = buildLocalAttachmentDescriptor({
            baseKey: `${descriptor.key}-eml-attachment-${index + 1}`,
            fileName: attachment?.filename || attachment?.name || 'Unnamed attachment',
            fileType: attachment?.mimeType || attachment?.contentType || '',
            fileExtension: '',
            content: attachment?.content,
            encoding: attachment?.encoding,
            backLabel: 'Back to Email',
            errorSetter: setDocumentViewerError,
            viewerContextType: descriptor?.viewerContextType || '',
            viewerContextDescription: descriptor?.viewerContextDescription || ''
          });

          return {
            fileName: attachmentDescriptor.fileName,
            mimeType: attachmentDescriptor.mimeType,
            size: attachmentDescriptor.localBlob.size,
            descriptor: attachmentDescriptor
          };
        })
        : []
    });
    setDocumentViewerError('');
  };

  const loadMsgPreview = async (descriptor, loadToken) => {
    const messageBuffer = await loadDescriptorArrayBuffer(descriptor);
    const msgReader = new MsgReader(new Uint8Array(messageBuffer));
    const parsedMessage = msgReader.getFileData();
    if (documentViewerLoadTokenRef.current !== loadToken) return;

    const recipients = Array.isArray(parsedMessage?.recipients) ? parsedMessage.recipients : [];
    const toRecipients = recipients.filter((recipient) => recipient?.recipType === 'to' || !recipient?.recipType);
    const ccRecipients = recipients.filter((recipient) => recipient?.recipType === 'cc');
    const bccRecipients = recipients.filter((recipient) => recipient?.recipType === 'bcc');
    const previewBody = getEmailPreviewBody({
      text: parsedMessage?.body,
      bodyHtml: parsedMessage?.bodyHtml
    });
    const sender = formatEmailAddressEntry({
      name: parsedMessage?.senderName,
      email: parsedMessage?.senderEmail || parsedMessage?.senderSmtpAddress || parsedMessage?.creatorSMTPAddress
    });

    setDocumentViewerEmailPreview({
      previewType: 'msg',
      subject: String(parsedMessage?.subject || '').trim(),
      from: sender,
      to: formatEmailAddressList(toRecipients),
      cc: formatEmailAddressList(ccRecipients),
      bcc: formatEmailAddressList(bccRecipients),
      date: formatEmailDateValue(
        parsedMessage?.messageDeliveryTime
          || parsedMessage?.clientSubmitTime
          || parsedMessage?.creationTime
      ),
      body: previewBody,
      attachments: Array.isArray(parsedMessage?.attachments)
        ? parsedMessage.attachments.map((attachment, index) => {
          const attachmentData = msgReader.getAttachment(attachment);
          const attachmentDescriptor = attachmentData?.content
            ? buildLocalAttachmentDescriptor({
              baseKey: `${descriptor.key}-msg-attachment-${index + 1}`,
              fileName: attachmentData.fileName
                || attachment?.fileName
                || attachment?.fileNameShort
                || attachment?.name
                || 'Unnamed attachment',
              fileType: attachment?.mimeType || '',
              fileExtension: attachment?.extension || '',
              content: attachmentData.content,
              backLabel: 'Back to Email',
              errorSetter: setDocumentViewerError,
              viewerContextType: descriptor?.viewerContextType || '',
              viewerContextDescription: descriptor?.viewerContextDescription || ''
            })
            : null;

          return {
            fileName: String(
              attachmentData?.fileName
                || attachment?.fileName
                || attachment?.fileNameShort
                || attachment?.name
                || 'Unnamed attachment'
            ),
            mimeType: attachmentDescriptor?.mimeType || String(attachment?.mimeType || attachment?.extension || ''),
            size: attachmentDescriptor?.localBlob?.size || Number(attachment?.contentLength || 0),
            descriptor: attachmentDescriptor
          };
        })
        : []
    });
    setDocumentViewerError('');
  };

  const resetDocumentViewerExcelViewport = () => {
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        if (documentViewerExcelWrapRef.current) {
          documentViewerExcelWrapRef.current.scrollLeft = 0;
          documentViewerExcelWrapRef.current.scrollTop = 0;
        }
      });
    }
  };

  const handleDocumentViewerSheetChange = (sheetName) => {
    const workbook = documentViewerWorkbookRef.current;
    if (!workbook?.Sheets?.[sheetName]) return;

    setDocumentViewerActiveSheetName(sheetName);
    setDocumentViewerExcelRows(getExcelSheetRows(workbook, sheetName));
    setDocumentViewerExcelFilters({});
    setDocumentViewerExcelSortConfig({ column: null, direction: 'asc' });
    resetDocumentViewerExcelViewport();
  };

  const handleDocumentViewerExcelFilterChange = (columnIndex, value) => {
    const nextValue = String(value || '');
    const next = { ...documentViewerExcelFilters };
    if (nextValue.trim()) next[columnIndex] = nextValue;
    else delete next[columnIndex];
    setDocumentViewerExcelFilters(next);
  };

  const handleDocumentViewerExcelSort = (columnIndex) => {
    const next = documentViewerExcelSortConfig.column === columnIndex
      ? { column: columnIndex, direction: documentViewerExcelSortConfig.direction === 'asc' ? 'desc' : 'asc' }
      : { column: columnIndex, direction: 'asc' };
    setDocumentViewerExcelSortConfig(next);
  };

  const buildCaoDocumentDescriptor = (row, errorSetter, fallbackCategoryId) => {
    if (!row?.FileID) return null;

    const categoryParam = Number(row.AttachmentCategoryID ?? fallbackCategoryId) || null;
    const selectedAttachmentTypeId = categoryParam === CONTRACTS_CATEGORY_ID ? contractsTypeId : typeId;
    const attachmentTypeId = Number(
      row.AttachmentTypeID
      ?? row.AttachmentTypeId
      ?? row.TypeID
      ?? row.TypeId
      ?? selectedAttachmentTypeId
      ?? 0
    ) || null;
    const countyParam = categoryParam === CONTRACTS_CATEGORY_ID ? contractsCountyId : chiefCountyId;
    const mimeType = resolveMimeType(row.FileType, row.FileName, row.FileExtension);
    const previewDisabledReason = getExcelPreviewDisabledReason(row);

    return {
      key: `cao-${row.FileID}`,
      endpoint: '/owners/owner-attachments/cao-documents/file',
      params: {
        fileId: row.FileID,
        ...(countyParam ? { countyId: countyParam } : {}),
        ...(categoryParam ? { categoryId: categoryParam } : {})
      },
      fileName: getResolvedFileName(row.FileName, row.FileExtension, `cao-document-${row.FileID}`),
      fileType: row.FileType,
      mimeType,
      fileExtension: normalizeFileExtension(row.FileExtension, row.FileName),
      fileTypeLabel: friendlyFileType(row.FileType, row.FileName, row.FileExtension),
      canPreview: !previewDisabledReason && canPreviewFile(row.FileType, row.FileName, row.FileExtension),
      previewDisabledReason,
      isImage: mimeType.startsWith('image/'),
      isPdf: mimeType === 'application/pdf',
      attachmentCategoryId: categoryParam,
      attachmentTypeId,
      ownerIndexedPdfEligible: categoryParam === CHIEF_CATEGORY_ID && attachmentTypeId === CHIEF_NOTICE_OF_VALUE_TYPE_ID,
      backLabel: 'Back to Documents',
      errorSetter
    };
  };

  const buildProtestAttachmentDescriptor = (attachment) => {
    if (!selectedProtestRow?.ProtestID || !attachment?.AttachmentID) return null;
    const mimeType = resolveMimeType(attachment.FileType, attachment.FileName, attachment.FileExtension);
    const previewDisabledReason = getExcelPreviewDisabledReason(attachment);

    return {
      key: `protest-${attachment.AttachmentID}`,
      endpoint: '/owners/protests/attachments/file',
      params: {
        protestId: selectedProtestRow.ProtestID,
        attachmentId: attachment.AttachmentID
      },
      fileName: getResolvedFileName(
        attachment.FileName,
        attachment.FileExtension,
        `protest-attachment-${attachment.AttachmentID}`
      ),
      fileType: attachment.FileType,
      mimeType,
      fileExtension: normalizeFileExtension(attachment.FileExtension, attachment.FileName),
      fileTypeLabel: friendlyFileType(attachment.FileType, attachment.FileName, attachment.FileExtension),
      canPreview: !previewDisabledReason && canPreviewFile(attachment.FileType, attachment.FileName, attachment.FileExtension),
      previewDisabledReason,
      isImage: mimeType.startsWith('image/'),
      isPdf: mimeType === 'application/pdf',
      backLabel: 'Back to Attachments',
      errorSetter: setProtestAttachmentsError,
      viewerContextType: attachment.AttachmentTypeDescription || attachment.AttachmentType || '',
      viewerContextDescription: attachment.Description || ''
    };
  };

  const handleEmailAttachmentAction = async (descriptor, requestedAction = OPEN_ACTION) => {
    if (requestedAction === DOWNLOAD_ACTION || !descriptor?.canPreview) {
      await handleFileAction(descriptor, requestedAction);
      return;
    }

    clearDescriptorError(descriptor);
    setPendingFileAction({ key: descriptor.key, mode: OPEN_ACTION });
    documentViewerHistoryRef.current.push({
      viewer: { ...documentViewer },
      emailPreview: documentViewerEmailPreview
    });

    try {
      await startInAppViewer(descriptor);
    } catch (err) {
      console.error('Failed to open email attachment preview:', err);
      documentViewerHistoryRef.current.pop();
      setDescriptorError(descriptor, 'Unable to open file.');
    } finally {
      window.setTimeout(() => {
        setPendingFileAction((current) => (
          current.key === descriptor.key && current.mode === OPEN_ACTION
            ? { key: '', mode: '' }
            : current
        ));
      }, 250);
    }
  };

  const startInAppViewer = async (descriptor) => {
    const isPdfFile = descriptor?.mimeType === 'application/pdf';
    const isExcelFile = isExcelMimeType(descriptor?.mimeType);
    const isEmlFile = isEmlMimeType(descriptor?.mimeType);
    const isMsgFile = isMsgMimeType(descriptor?.mimeType);
    let previewUrl = '';

    if (isEmlFile || isMsgFile) {
      closeDocumentViewer();
      setSharedDocumentViewer(descriptor);
      return;
    }

    cancelDocumentViewerPdfRevealAnimationFrame();
    revokeDocumentViewerLocalUrl();
    if (!isExcelFile && !isEmlFile && !isMsgFile) {
      previewUrl = descriptor?.localBlob instanceof Blob
        ? URL.createObjectURL(descriptor.localBlob)
        : buildDescriptorUrl(descriptor);
      documentViewerLocalUrlRef.current = descriptor?.localBlob instanceof Blob ? previewUrl : '';
    }

    const loadToken = documentViewerLoadTokenRef.current + 1;
    documentViewerLoadTokenRef.current = loadToken;
    setDocumentViewerPageCount(0);
    setDocumentViewerCurrentPage(1);
    setDocumentViewerDisplayedPdfPage(1);
    setDocumentViewerPageInput('1');
    setDocumentViewerError('');
    setDocumentViewerLoading(isPdfFile || isExcelFile || isEmlFile || isMsgFile);
    setDocumentViewerPdfPageLoading(isPdfFile);
    setDocumentViewerSheetNames([]);
    setDocumentViewerActiveSheetName('');
    setDocumentViewerExcelRows([]);
    setDocumentViewerExcelFilters({});
    setDocumentViewerExcelSortConfig({ column: null, direction: 'asc' });
    setDocumentViewerEmailPreview(null);
    setDocumentViewerPdfOwnerIndexState(createEmptyPdfOwnerIndexState());
    setDocumentViewerPdfOwnerQuery('');
    setDocumentViewerPdfOwnerDownloadKey('');
    setDocumentViewerPdfOwnerDownloadError('');
    setDocumentViewerPdfRangeStartInput('1');
    setDocumentViewerPdfRangeEndInput('1');
    setDocumentViewerPdfRangeActionMode('');
    setDocumentViewerPdfRangeError('');
    setDocumentViewerScale(getDefaultDocumentViewerScale());
    setDocumentViewerRotation(0);
    documentViewerPageRefs.current = [];
    documentViewerPdfRenderStateRef.current = { pageNumber: 0, canvasReady: false, textLayerReady: false };
    documentViewerWorkbookRef.current = null;
    setDocumentViewer({
      open: true,
      previewUrl,
      fileName: descriptor.fileName,
      fileTypeLabel: descriptor.fileTypeLabel,
      mimeType: descriptor.mimeType,
      backLabel: descriptor.backLabel,
      resource: descriptor
    });

    if (!isExcelFile && !isEmlFile && !isMsgFile) return;

    try {
      if (isExcelFile) {
        await loadExcelWorkbook(descriptor, loadToken);
        resetDocumentViewerExcelViewport();
      } else if (isEmlFile) {
        await loadEmlPreview(descriptor, loadToken);
      } else {
        await loadMsgPreview(descriptor, loadToken);
      }
    } catch (err) {
      console.error(
        isExcelFile
          ? 'Failed to load Excel preview:'
          : isMsgFile
            ? 'Failed to load Outlook email preview:'
            : 'Failed to load email preview:',
        err
      );
      if (documentViewerLoadTokenRef.current !== loadToken) return;
      setDocumentViewerError(
        isExcelFile
          ? (err?.message || 'Unable to load Excel preview. Use Download to save this file.')
          : isMsgFile
            ? 'Unable to load Outlook email preview. Use Download to save this file.'
            : 'Unable to load email preview. Use Download to save this file.'
      );
    } finally {
      if (documentViewerLoadTokenRef.current === loadToken) {
        setDocumentViewerLoading(false);
      }
    }
  };

  const handleFileAction = async (descriptor, requestedAction = OPEN_ACTION) => {
    if (!descriptor) return;

    const resolvedAction = descriptor.canPreview && requestedAction === OPEN_ACTION
      ? OPEN_ACTION
      : DOWNLOAD_ACTION;

    clearDescriptorError(descriptor);
    setPendingFileAction({ key: descriptor.key, mode: resolvedAction });

    try {
      if (resolvedAction === DOWNLOAD_ACTION) {
        triggerDirectDownload(descriptor);
        return;
      }

      if (descriptor.canPreview) {
        await startInAppViewer(descriptor);
        return;
      }
    } catch (err) {
      console.error('Failed to handle file action:', err);
      setDescriptorError(
        descriptor,
        err?.message || (resolvedAction === DOWNLOAD_ACTION
          ? 'Unable to download file.'
          : 'Unable to open file.')
      );
    } finally {
      window.setTimeout(() => {
        setPendingFileAction((current) => (
          current.key === descriptor.key && current.mode === resolvedAction
            ? { key: '', mode: '' }
            : current
        ));
      }, 250);
    }
  };

  useEffect(() => {
    return () => {
      cancelDocumentViewerPdfRevealAnimationFrame();
      revokeDocumentViewerLocalUrl();
    };
  }, []);

  useEffect(() => {
    if (!documentViewer.open || documentViewer.mimeType !== 'application/pdf') return undefined;

    const updateViewerWidth = () => {
      const containerWidth = documentViewerBodyRef.current?.clientWidth || 0;
      setDocumentViewerWidth(containerWidth ? Math.max(240, containerWidth - 32) : 0);
    };

    updateViewerWidth();

    if (typeof ResizeObserver === 'function' && documentViewerBodyRef.current) {
      const observer = new ResizeObserver(updateViewerWidth);
      observer.observe(documentViewerBodyRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateViewerWidth);
    return () => window.removeEventListener('resize', updateViewerWidth);
  }, [documentViewer.open, documentViewer.mimeType]);

  const adjustDocumentViewerScale = (direction) => {
    setDocumentViewerScale((current) => {
      const nextScale = direction === 'in' ? current + PDF_SCALE_STEP : current - PDF_SCALE_STEP;
      return Math.min(MAX_PDF_SCALE, Math.max(MIN_PDF_SCALE, Number(nextScale.toFixed(2))));
    });
  };

  useEffect(() => {
    setDocumentViewerZoomInput(String(Math.round(documentViewerScale * 100)));
  }, [documentViewerScale]);

  const applyDocumentViewerZoomInput = () => {
    const requestedPercent = Number(documentViewerZoomInput);
    if (!Number.isFinite(requestedPercent)) {
      setDocumentViewerZoomInput(String(Math.round(documentViewerScale * 100)));
      return;
    }

    const minimumPercent = Math.round(MIN_PDF_SCALE * 100);
    const maximumPercent = Math.round(MAX_PDF_SCALE * 100);
    const clampedPercent = Math.min(maximumPercent, Math.max(minimumPercent, requestedPercent));
    setDocumentViewerScale(Number((clampedPercent / 100).toFixed(2)));
    setDocumentViewerZoomInput(String(Math.round(clampedPercent)));
  };

  const rotateDocumentViewer = () => {
    setDocumentViewerRotation((current) => (current + 90) % 360);
  };

  const goToDocumentViewerPage = (pageNumber) => {
    if (!documentViewerPageCount) return;

    const nextPage = Math.max(1, Math.min(documentViewerPageCount, Number(pageNumber) || 1));
    if (nextPage === documentViewerCurrentPage) {
      setDocumentViewerPageInput(String(nextPage));
      return;
    }

    if (isPdfViewerOpen) {
      cancelDocumentViewerPdfRevealAnimationFrame();
      documentViewerPdfRenderStateRef.current = {
        pageNumber: nextPage,
        canvasReady: false,
        textLayerReady: false
      };
      setDocumentViewerPdfPageLoading(true);
    }

    setDocumentViewerCurrentPage(nextPage);
    setDocumentViewerPageInput(String(nextPage));

    if (documentViewerBodyRef.current) {
      documentViewerBodyRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleDocumentViewerPageSubmit = () => {
    goToDocumentViewerPage(documentViewerPageInput);
  };

  const handleDocumentViewerOwnerJump = (entry) => {
    if (!entry) return;
    setDocumentViewerPdfOwnerQuery(entry.displayName);
    goToDocumentViewerPage(entry.startPage);
  };

  const handleDocumentViewerOwnerDownload = async (entry) => {
    if (!entry || documentViewerPdfOwnerIndexState.status !== 'ready' || !documentViewer.resource) return;

    const ownerResultKey = getPdfOwnerResultKey(entry);
    setDocumentViewerPdfOwnerDownloadKey(ownerResultKey);
    setDocumentViewerPdfOwnerDownloadError('');

    try {
      const { blob, startPage, endPage } = await buildDocumentViewerPdfRangeBlob(
        documentViewer.resource,
        entry.startPage,
        entry.endPage
      );
      triggerBlobDownload(
        blob,
        buildPdfOwnerRangeFileName(documentViewer.resource.fileName, { ...entry, startPage, endPage })
      );
    } catch (err) {
      console.error('Failed to download owner page range:', err);
      setDocumentViewerPdfOwnerDownloadError(err?.message || 'Unable to prepare the selected owner pages for download.');
    } finally {
      setDocumentViewerPdfOwnerDownloadKey((current) => (
        current === ownerResultKey ? '' : current
      ));
    }
  };

  const handleDocumentViewerPdfRangeDownload = async () => {
    if (!documentViewer.resource || !documentViewerPageCount) return;

    setDocumentViewerPdfRangeActionMode('download');
    setDocumentViewerPdfRangeError('');

    try {
      const { blob, startPage, endPage } = await buildDocumentViewerPdfRangeBlob(
        documentViewer.resource,
        documentViewerPdfRangeStartInput,
        documentViewerPdfRangeEndInput
      );

      triggerBlobDownload(
        blob,
        buildPdfPageRangeFileName(documentViewer.resource.fileName, startPage, endPage)
      );
    } catch (err) {
      console.error('Failed to download selected PDF page range:', err);
      setDocumentViewerPdfRangeError(err?.message || 'Unable to prepare the selected pages for download.');
    } finally {
      setDocumentViewerPdfRangeActionMode((current) => (current === 'download' ? '' : current));
    }
  };

  const handleDocumentViewerPdfRangePrint = async () => {
    if (!documentViewer.resource || !documentViewerPageCount) return;

    setDocumentViewerPdfRangeActionMode('print');
    setDocumentViewerPdfRangeError('');

    try {
      const { blob } = await buildDocumentViewerPdfRangeBlob(
        documentViewer.resource,
        documentViewerPdfRangeStartInput,
        documentViewerPdfRangeEndInput
      );

      await printPdfBlob(blob);
    } catch (err) {
      console.error('Failed to print selected PDF page range:', err);
      setDocumentViewerPdfRangeError(err?.message || 'Unable to open the selected pages for printing.');
    } finally {
      setDocumentViewerPdfRangeActionMode((current) => (current === 'print' ? '' : current));
    }
  };

  const handleDocumentViewerPdfLoadSuccess = (pdf) => {
    documentViewerPdfRef.current = pdf;
    const { numPages } = pdf;
    setDocumentViewerPageCount(numPages);
    setDocumentViewerCurrentPage(1);
    setDocumentViewerDisplayedPdfPage(1);
    documentViewerPdfRenderStateRef.current = {
      pageNumber: 1,
      canvasReady: false,
      textLayerReady: false
    };
    setDocumentViewerError('');
  };

  const handleDocumentViewerPdfLoadFailure = (err, message) => {
    console.error(message, err);
    documentViewerPdfRef.current = null;
    setDocumentViewerPageCount(0);
    setDocumentViewerCurrentPage(1);
    setDocumentViewerDisplayedPdfPage(1);
    setDocumentViewerLoading(false);
    setDocumentViewerPdfPageLoading(false);
    documentViewerPdfRenderStateRef.current = { pageNumber: 0, canvasReady: false, textLayerReady: false };
    setDocumentViewerError('Unable to load PDF preview. Use Download to save this file.');
  };

  const markDocumentViewerPdfRenderStageComplete = (pageNumber, stage) => {
    const renderState = documentViewerPdfRenderStateRef.current;
    if (renderState.pageNumber !== pageNumber) return;

    if (stage === 'canvas') {
      renderState.canvasReady = true;
    } else if (stage === 'text') {
      renderState.textLayerReady = true;
    }

    if (!renderState.canvasReady || !renderState.textLayerReady) return;

    cancelDocumentViewerPdfRevealAnimationFrame();

    const finishReveal = () => {
      if (documentViewerPdfRenderStateRef.current.pageNumber !== pageNumber) return;
      if (!isLargePdfViewerOpen) {
        setDocumentViewerDisplayedPdfPage(pageNumber);
      }
      setDocumentViewerLoading(false);
      setDocumentViewerPdfPageLoading(false);
    };

    if (isLargePdfViewerOpen || typeof window === 'undefined') {
      finishReveal();
      return;
    }

    documentViewerPdfRevealAnimationFrameRef.current = window.requestAnimationFrame(() => {
      documentViewerPdfRevealAnimationFrameRef.current = window.requestAnimationFrame(() => {
        documentViewerPdfRevealAnimationFrameRef.current = 0;
        finishReveal();
      });
    });
  };

  const handleDocumentViewerPdfPageRenderSuccess = (pageNumber) => {
    markDocumentViewerPdfRenderStageComplete(pageNumber, 'canvas');
  };

  const handleDocumentViewerPdfTextLayerRenderSuccess = (pageNumber) => {
    markDocumentViewerPdfRenderStageComplete(pageNumber, 'text');
  };

  const handleDocumentViewerPdfTextLayerRenderError = (err, pageNumber) => {
    console.warn('Failed to render PDF text layer:', err);
    markDocumentViewerPdfRenderStageComplete(pageNumber, 'text');
  };

  const handleDocumentViewerPdfPageRenderError = (err) => {
    console.error('Failed to render PDF page:', err);
    setDocumentViewerLoading(false);
    setDocumentViewerPdfPageLoading(false);
    setDocumentViewerError('Unable to render PDF preview. Use Download to save this file.');
  };

  useEffect(() => {
    if (!isPdfViewerOpen || !documentViewerPageCount) return;
    setDocumentViewerPageInput(String(documentViewerCurrentPage));
  }, [documentViewerCurrentPage, documentViewerPageCount, isPdfViewerOpen]);

  useEffect(() => {
    if (!isPdfViewerOpen || !isPdfViewerReadyToRender || documentViewerError) return;

    documentViewerPdfRenderStateRef.current = {
      pageNumber: documentViewerCurrentPage,
      canvasReady: false,
      textLayerReady: false
    };
    setDocumentViewerPdfPageLoading(true);
  }, [
    documentViewerCurrentPage,
    documentViewerError,
    documentViewerRotation,
    documentViewerScale,
    documentViewerWidth,
    isPdfViewerOpen,
    isPdfViewerReadyToRender
  ]);

  useEffect(() => {
    if (!isLargePdfViewerOpen || !documentViewer.open || !documentViewerPageCount || !documentViewerPdfRef.current) {
      return undefined;
    }

    const pdfDocument = documentViewerPdfRef.current;
    const cacheKey = getPdfOwnerIndexCacheKey(documentViewer.resource);
    const cachedState = cacheKey ? pdfOwnerIndexCache.get(cacheKey) : null;
    if (cachedState) {
      setDocumentViewerPdfOwnerIndexState(cachedState);
      return undefined;
    }

    const viewerToken = documentViewerLoadTokenRef.current;
    let cancelled = false;

    const runOwnerIndexScan = async () => {
      setDocumentViewerPdfOwnerIndexState({
        status: 'checking',
        scannedPages: 0,
        totalPages: documentViewerPageCount,
        entries: [],
        error: ''
      });

      try {
        const firstEntry = await extractPdfOwnerNoticeEntry(pdfDocument, 1);
        if (cancelled || documentViewerLoadTokenRef.current !== viewerToken) return;

        if (!firstEntry) {
          setDocumentViewerPdfOwnerIndexState({
            status: 'unsupported',
            scannedPages: 1,
            totalPages: documentViewerPageCount,
            entries: [],
            error: ''
          });
          return;
        }

        const entries = [{
          ...firstEntry,
          startPage: 1,
          endPage: documentViewerPageCount
        }];

        setDocumentViewerPdfOwnerIndexState({
          status: 'indexing',
          scannedPages: 1,
          totalPages: documentViewerPageCount,
          entries: buildIndexedOwnerEntriesSnapshot(entries, 1, documentViewerPageCount),
          error: ''
        });

        let scannedPages = 1;
        for (let pageNumber = 2; pageNumber <= documentViewerPageCount; pageNumber += 1) {
          const ownerEntry = await extractPdfOwnerNoticeEntry(pdfDocument, pageNumber);
          if (cancelled || documentViewerLoadTokenRef.current !== viewerToken) return;

          scannedPages = pageNumber;
          if (ownerEntry) {
            entries[entries.length - 1].endPage = pageNumber - 1;
            entries.push({
              ...ownerEntry,
              startPage: pageNumber,
              endPage: documentViewerPageCount
            });
          }

          if (pageNumber % PDF_OWNER_INDEX_BATCH_SIZE === 0 || pageNumber === documentViewerPageCount) {
            setDocumentViewerPdfOwnerIndexState({
              status: pageNumber === documentViewerPageCount ? 'ready' : 'indexing',
              scannedPages,
              totalPages: documentViewerPageCount,
              entries: buildIndexedOwnerEntriesSnapshot(entries, scannedPages, documentViewerPageCount),
              error: ''
            });

            if (pageNumber < documentViewerPageCount) {
              await new Promise((resolve) => window.setTimeout(resolve, 0));
            }
          }
        }

        const finalState = {
          status: 'ready',
          scannedPages: documentViewerPageCount,
          totalPages: documentViewerPageCount,
          entries: buildIndexedOwnerEntriesSnapshot(entries, documentViewerPageCount, documentViewerPageCount),
          error: ''
        };

        if (cacheKey) {
          pdfOwnerIndexCache.set(cacheKey, finalState);
        }

        if (!cancelled && documentViewerLoadTokenRef.current === viewerToken) {
          setDocumentViewerPdfOwnerIndexState(finalState);
        }
      } catch (err) {
        console.error('Failed to build PDF owner index:', err);
        if (cancelled || documentViewerLoadTokenRef.current !== viewerToken) return;

        setDocumentViewerPdfOwnerIndexState({
          status: 'error',
          scannedPages: 0,
          totalPages: documentViewerPageCount,
          entries: [],
          error: 'Unable to build owner index for this PDF.'
        });
      }
    };

    const timeoutId = window.setTimeout(() => {
      runOwnerIndexScan();
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [documentViewer.open, documentViewer.resource, documentViewerPageCount, isLargePdfViewerOpen]);

  useEffect(() => {
    if (!isPdfViewerOpen || !documentViewerPageCount || !documentViewerBodyRef.current || documentViewerRenderedPdfPageCount <= 1) {
      return undefined;
    }

    const root = documentViewerBodyRef.current;
    const visiblePages = new Map();

    const resolveCurrentPage = () => {
      if (visiblePages.size > 0) {
        const bestMatch = [...visiblePages.entries()]
          .sort((a, b) => {
            if (b[1] !== a[1]) return b[1] - a[1];
            return a[0] - b[0];
          })[0];

        if (bestMatch) {
          setDocumentViewerCurrentPage(bestMatch[0]);
          return;
        }
      }

      const rootTop = root.getBoundingClientRect().top;
      let nearestPage = 1;
      let nearestDistance = Number.POSITIVE_INFINITY;

      documentViewerPageRefs.current.forEach((node, index) => {
        if (!node) return;
        const distance = Math.abs(node.getBoundingClientRect().top - rootTop);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPage = index + 1;
        }
      });

      setDocumentViewerCurrentPage(nearestPage);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageNumber = Number(entry.target.getAttribute('data-page-number'));
          if (!pageNumber) return;

          if (entry.isIntersecting) {
            visiblePages.set(pageNumber, entry.intersectionRatio);
          } else {
            visiblePages.delete(pageNumber);
          }
        });

        resolveCurrentPage();
      },
      {
        root,
        threshold: [0.2, 0.4, 0.6, 0.8]
      }
    );

    documentViewerPageRefs.current.forEach((node) => {
      if (node) observer.observe(node);
    });

    resolveCurrentPage();

    return () => observer.disconnect();
  }, [documentViewerPageCount, documentViewerRenderedPdfPageCount, documentViewerScale, documentViewerRotation, isPdfViewerOpen]);

  const renderFileActions = (
    descriptor,
    emptyLabel = 'No File',
    className = '',
    actionHandler = handleFileAction,
    options = {}
  ) => {
    if (!descriptor) {
      return <span style={{ color: '#888' }}>{emptyLabel}</span>;
    }

    const { showDownload = true, iconOnly = false, downloadIconOnly = false } = options;
    const isOpenPending = pendingFileAction.key === descriptor.key && pendingFileAction.mode === OPEN_ACTION;
    const isDownloadPending = pendingFileAction.key === descriptor.key && pendingFileAction.mode === DOWNLOAD_ACTION;
    const isActionPending = isOpenPending || isDownloadPending;
    const wrapperClassName = className ? `cao-file-actions ${className}` : 'cao-file-actions';

    if (!descriptor.canPreview && !showDownload) {
      return <span style={{ color: '#888' }}>No preview</span>;
    }

    return (
      <div className={wrapperClassName}>
        {(descriptor.canPreview || descriptor.previewDisabledReason) && (
          <button
            type="button"
            className={iconOnly ? 'view-button cao-file-actions__icon-button' : 'view-button'}
            disabled={isActionPending || Boolean(descriptor.previewDisabledReason)}
            onClick={() => actionHandler(descriptor, OPEN_ACTION)}
            aria-label={descriptor.previewDisabledReason || (isOpenPending ? 'Opening attachment' : 'View attachment')}
            title={descriptor.previewDisabledReason || (isOpenPending ? 'Opening attachment' : 'View attachment')}
          >
            {iconOnly ? renderFileActionIcon(OPEN_ACTION) : (isOpenPending ? 'Opening...' : 'View')}
          </button>
        )}
        {showDownload && (
          <button
            type="button"
            className={iconOnly || downloadIconOnly ? 'view-button cao-file-actions__icon-button' : 'view-button'}
            disabled={isActionPending}
            onClick={() => actionHandler(descriptor, DOWNLOAD_ACTION)}
            aria-label={isDownloadPending ? 'Downloading attachment' : 'Download attachment'}
            title={isDownloadPending ? 'Downloading attachment' : 'Download attachment'}
          >
            {iconOnly || downloadIconOnly
              ? renderFileActionIcon(DOWNLOAD_ACTION)
              : (isDownloadPending ? 'Downloading...' : 'Download')}
          </button>
        )}
      </div>
    );
  };

  const loadSharedDocument = async () => {
    const descriptor = sharedDocumentViewer;
    if (!descriptor) throw new Error('No document selected.');
    if (descriptor.localBlob instanceof Blob) return descriptor.localBlob;

    return apiClient.get(descriptor.endpoint, {
      params: descriptor.params || {},
      responseType: 'blob',
      withCredentials: true
    });
  };

  const sharedViewerFile = sharedDocumentViewer
    ? {
      FileID: sharedDocumentViewer.key,
      FileName: sharedDocumentViewer.fileName,
      FileType: sharedDocumentViewer.mimeType,
      FileExtension: sharedDocumentViewer.fileExtension,
      AttachmentTypeID: sharedDocumentViewer.attachmentTypeId,
      FileSizeBytes: sharedDocumentViewer.localBlob?.size || 0
    }
    : null;

  const sharedViewerMetadata = sharedDocumentViewer
    ? [
      { label: 'Owner Name', value: documentViewerOwnerName },
      { label: 'Protest Type', value: documentViewerProtestType },
      { label: 'Type', value: sharedDocumentViewer.viewerContextType || sharedDocumentViewer.fileTypeLabel || '-' },
      { label: 'Description', value: sharedDocumentViewer.viewerContextDescription || '-' }
    ]
    : [];

  const handleOpenProtestAttachments = async (row) => {
    if (!row?.ProtestID || protestAttachmentsLoading) return;

    setSelectedProtestRow(row);
    setShowProtestAttachmentsModal(true);
    setProtestAttachmentsLoading(true);
    setProtestAttachmentsError('');

    try {
      const res = await apiClient.get('/owners/protests/attachments', {
        params: { protestId: row.ProtestID },
        withCredentials: true
      });
      setProtestAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch protest attachments:', err);
      setProtestAttachments([]);
      setProtestAttachmentsError('Unable to load protest attachments.');
    } finally {
      setProtestAttachmentsLoading(false);
    }
  };

  const closeProtestAttachmentsModal = () => {
    closeDocumentViewer();
    setShowProtestAttachmentsModal(false);
    setProtestAttachments([]);
    setProtestAttachmentsError('');
    setSelectedProtestRow(null);
  };

  return (
    <div style={{ ...styles.mainContainer, margin: '25px auto' }} className="cao-page">
      <Navbar username={username} isAdmin={isAdmin} onLogout={onLogout} />

      <div className="owner-container cao-container">
        <h2 className="cao-page__title" style={styles.heading}>CHIEF DASHBOARD </h2>

        {/* Chief section */}
        <div className="cao-section cao-section--chief" style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={() => setChiefCollapsed((v) => !v)}
              className="view-button"
              style={{ padding: '6px 10px' }}
              aria-label={chiefCollapsed ? 'Expand section' : 'Collapse section'}
            >
              {chiefCollapsed ? '▲' : '▼'}
            </button>
          </div>

          {!chiefCollapsed && (
            <>
              <div className="owner-filters">
                {isAdmin && (
                  <div className="owner-section owner-section--county">
                    <label className="owner-select-label" htmlFor="cao-county-chief">County:</label>
                    <select
                      id="cao-county-chief"
                      className="owner-select owner-select--county"
                      value={chiefCountyId || ''}
                      onChange={(e) => setChiefCountyId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">-- Select County --</option>
                      {counties.map((c) => (
                        <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                      ))}
                    </select>
                    {!chiefCountyId && (
                      <p className="owner-no-records" style={{ marginTop: 8 }}>
                        Please select a county.
                      </p>
                    )}
                  </div>
                )}

                {shouldShowChief && (
                  <div className="owner-controls cao-controls">
                    <div className="owner-control owner-control--year">
                      <label className="owner-select-label" htmlFor="cao-year">Appraisal Year:</label>
                      <select
                        id="cao-year"
                        className="owner-select"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                      >
                        {Array.from({ length: 7 }, (_, i) => 2026 - i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="cao-type">Type (Chief):</label>
                      <select
                        id="cao-type"
                        className="owner-select"
                        value={typeId ?? ''}
                        onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : null)}
                        disabled={!typeOptions.length}
                      >
                        {typeOptions.map((t) => {
                          const optionId = t.ID ?? t.id;
                          const optionName = t.Name ?? t.name;
                          return <option key={optionId} value={optionId}>{optionName}</option>;
                        })}
                        {!typeOptions.length && <option value="">No types available</option>}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {shouldShowChief && (
                <div className="owner-table-wrapper">
                  {loading && <p style={{ marginTop: 20 }}>Loading data...</p>}
                  {!loading && error && (
                    <p className="owner-inline-error owner-inline-error--block">{error}</p>
                  )}
                  {!loading && !error && records.length === 0 && canFetch && (
                    <p className="owner-no-records">No records found{year ? ` for ${year}` : ''}.</p>
                  )}
                  {!loading && !error && records.length > 0 && (
                    <table className="owner-table cao-table">
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>File Type</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row) => {
                          const descriptor = buildCaoDocumentDescriptor(row, setError, CHIEF_CATEGORY_ID);

                          return (
                            <tr key={row.FileID || `${row.FileName}-${row.UploadDate}`}>
                              <td>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={isAdmin && Boolean(row.IsAdminOnly) ? { color: '#e11d48', fontWeight: 600 } : {}}>
                                    {row.FileName || '-'}
                                  </span>
                                  {isAdmin && Boolean(row.IsAdminOnly) && (
                                    <em style={{ color: '#e11d48', fontSize: 14, fontStyle: 'italic', fontWeight: 700 }}>Int</em>
                                  )}
                                </span>
                              </td>
                              <td>{row.Description || '-'}</td>
                              <td>{renderTypeName(row.AttachmentTypeID)}</td>
                              <td>{friendlyFileType(row.FileType, row.FileName, row.FileExtension)}</td>
                              <td className="text-center">
                                {renderFileActions(
                                  descriptor,
                                  'No File',
                                  '',
                                  handleFileAction,
                                  { downloadIconOnly: true }
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Contracts section (visible when restricted flag is true, for any user) */}
        {restricted && (
          <div className="cao-section cao-section--contracts" style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <h3 style={{ ...styles.heading, marginTop: 0, marginBottom: 0, flex: 1, textAlign: 'center' }}>Contracts</h3>
              <button
                type="button"
                onClick={() => setContractsCollapsed((v) => !v)}
                className="view-button"
                style={{ padding: '6px 10px' }}
              >
                {contractsCollapsed ? '▲' : '▼'}
              </button>
            </div>

            {!contractsCollapsed && (
              <>
                <div className="owner-filters">
                  {isAdmin && (
                    <div className="owner-section owner-section--county">
                      <label className="owner-select-label" htmlFor="cao-county-contracts">County:</label>
                      <select
                        id="cao-county-contracts"
                        className="owner-select owner-select--county"
                        value={contractsCountyId || ''}
                        onChange={(e) => setContractsCountyId(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">-- Select County --</option>
                        {counties.map((c) => (
                          <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                        ))}
                      </select>
                      {!contractsCountyId && (
                        <p className="owner-no-records" style={{ marginTop: 8 }}>
                          Please select a county.
                        </p>
                      )}
                    </div>
                  )}

                  {(!isAdmin || contractsCountyId) && (
                    <div className="owner-controls cao-controls">
                      <div className="owner-control owner-control--year">
                        <label className="owner-select-label" htmlFor="contracts-year">Appraisal Year:</label>
                        <select
                          id="contracts-year"
                          className="owner-select"
                          value={year}
                          onChange={(e) => setYear(Number(e.target.value))}
                        >
                          {Array.from({ length: 7 }, (_, i) => 2026 - i).map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>

                      <div className="owner-control">
                        <label className="owner-select-label" htmlFor="contracts-type">Type (Contracts):</label>
                        <select
                          id="contracts-type"
                          className="owner-select"
                          value={contractsTypeId ?? ''}
                          onChange={(e) => setContractsTypeId(e.target.value ? Number(e.target.value) : null)}
                          disabled={!contractsTypeOptions.length}
                        >
                          {contractsTypeOptions.map((t) => {
                            const optionId = t.ID ?? t.id;
                            const optionName = t.Name ?? t.name;
                            return <option key={optionId} value={optionId}>{optionName}</option>;
                          })}
                          {!contractsTypeOptions.length && <option value="">No types available</option>}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {(!isAdmin || contractsCountyId) && (
                  <div className="owner-table-wrapper">
                    {contractsLoading && <p style={{ marginTop: 20 }}>Loading data...</p>}
                    {!contractsLoading && contractsError && (
                      <p className="owner-inline-error owner-inline-error--block">{contractsError}</p>
                    )}
                    {!contractsLoading && !contractsError && contractsRecords.length === 0 && canFetchContracts && (
                      <p className="owner-no-records">No records found{year ? ` for ${year}` : ''}.</p>
                    )}
                    {!contractsLoading && !contractsError && contractsRecords.length > 0 && (
                      <table className="owner-table cao-table">
                        <thead>
                          <tr>
                            <th>File Name</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th>File Type</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contractsRecords.map((row) => {
                            const descriptor = buildCaoDocumentDescriptor(row, setContractsError, CONTRACTS_CATEGORY_ID);

                            return (
                              <tr key={row.FileID || `${row.FileName}-${row.UploadDate}`}>
                                <td>{row.FileName || '-'}</td>
                                <td>{row.Description || '-'}</td>
                                <td>{renderContractsTypeName(row.AttachmentTypeID)}</td>
                                <td>{friendlyFileType(row.FileType, row.FileName, row.FileExtension)}</td>
                                <td className="text-center">
                                  {renderFileActions(descriptor)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Protest tracker */}
        <div className="cao-section cao-section--protests" style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <h3 style={{ ...styles.heading, marginTop: 0, marginBottom: 0, flex: 1, textAlign: 'center' }}>Protest Tracker</h3>
            <button
              type="button"
              onClick={() => setProtestCollapsed((v) => !v)}
              className="view-button"
              style={{ padding: '6px 10px' }}
            >
              {protestCollapsed ? '▲' : '▼'}
            </button>
          </div>

          {!protestCollapsed && (
            <>
              <div className="owner-filters">
                {isAdmin && (
                  <div className="owner-section owner-section--county">
                    <label className="owner-select-label" htmlFor="protest-county">County:</label>
                    <select
                      id="protest-county"
                      className="owner-select owner-select--county"
                      value={protestCountyId || ''}
                      onChange={(e) => {
                        const val = e.target.value ? Number(e.target.value) : null;
                        setProtestCountyId(val);
                        setAssignedName('');
                      }}
                    >
                      <option value="">-- Select County --</option>
                      {counties.map((c) => (
                        <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                      ))}
                    </select>
                    {!protestCountyId && (
                      <p className="owner-no-records" style={{ marginTop: 8 }}>
                        Please select a county.
                      </p>
                    )}
                  </div>
                )}

                {(!isAdmin || protestCountyId) && (
                  <div className="owner-controls cao-controls" style={{ flexWrap: 'wrap', gap: 12 }}>
                    <div className="owner-control owner-control--year">
                      <label className="owner-select-label" htmlFor="protest-year">Appraisal Year:</label>
                      <select
                        id="protest-year"
                        className="owner-select"
                        value={protestYear}
                        onChange={(e) => setProtestYear(Number(e.target.value))}
                      >
                        {Array.from({ length: 7 }, (_, i) => 2026 - i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>

                    {isAdmin && (
                      <div className="owner-control">
                        <label className="owner-select-label" htmlFor="protest-status">{protestStatusLabel}:</label>
                        <select
                          id="protest-status"
                          className="owner-select"
                          value={protestStatusCode}
                          onChange={(e) => setProtestStatusCode(e.target.value)}
                          disabled={!protestStatusOptions.length}
                        >
                          <option value="">All</option>
                          {protestStatusOptions.map((s) => (
                            <option key={s.code} value={s.code}>{s.label}</option>
                          ))}
                          {!protestStatusOptions.length && <option value="">No TYP statuses available</option>}
                        </select>
                      </div>
                    )}

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="protest-client-status">{protestClientStatusLabel}:</label>
                      <select
                        id="protest-client-status"
                        className="owner-select"
                        value={protestClientStatusCode}
                        onChange={(e) => setProtestClientStatusCode(e.target.value)}
                        disabled={!protestClientStatusOptions.length}
                      >
                        <option value="">All</option>
                        {protestClientStatusOptions.map((s, idx) => (
                          <option key={`${s.code}-${idx}`} value={s.code}>{s.label}</option>
                        ))}
                        {!protestClientStatusOptions.length && (
                          <option value="">{isAdmin ? 'No client statuses available' : 'No statuses available'}</option>
                        )}
                      </select>
                    </div>

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="protest-type">Protest Type:</label>
                      <select
                        id="protest-type"
                        className="owner-select"
                        value={protestTypeId ?? ''}
                        onChange={(e) => setProtestTypeId(e.target.value ? Number(e.target.value) : null)}
                        disabled={!visibleProtestTypeOptions.length}
                      >
                        <option value="">All Types</option>
                        {visibleProtestTypeOptions.map((t) => {
                          const optionId = t.Id ?? t.ID ?? t.id;
                          const optionName = t.Description ?? t.Name ?? t.name;
                          return <option key={optionId} value={optionId}>{optionName}</option>;
                        })}
                        {!visibleProtestTypeOptions.length && <option value="">No protest types available</option>}
                      </select>
                    </div>

                    <div className="owner-control">
                      <label className="owner-select-label" htmlFor="protest-assigned">Assigned:</label>
                      <select
                        id="protest-assigned"
                        className="owner-select"
                        value={assignedName}
                        onChange={(e) => setAssignedName(e.target.value)}
                      >
                        <option value="">All</option>
                        {assignedOptions.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                        {!assignedOptions.length && <option value="">No assignees for this county/year</option>}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {(!isAdmin || protestCountyId) && (
                <div className="owner-table-wrapper">
                  {protestLoading && <p style={{ marginTop: 20 }}>Loading data...</p>}
                  {!protestLoading && protestError && (
                    <p className="owner-inline-error owner-inline-error--block">{protestError}</p>
                  )}
                  {!protestLoading && !protestError && protestRecords.length === 0 && canFetchProtests && (
                    <p className="owner-no-records">No protest records found{protestYear ? ` for ${protestYear}` : ''}.</p>
                  )}
                  {!protestLoading && !protestError && protestRecords.length > 0 && (
                    <table className="owner-table cao-table cao-table--protests">
                      <thead>
                        <tr>
                          <th>County</th>
                          {renderProtestSortableHeader('ReceivedByTYP', 'Received BY Typ')}
                          {renderProtestSortableHeader('OwnerName', 'Owner')}
                          {renderProtestSortableHeader('ProtestTypeDescription', 'Protest Type')}
                          {renderProtestSortableHeader('HearingDate', 'Hearing Date')}
                          {isAdmin && renderProtestSortableHeader('Status', protestStatusLabel)}
                          {renderProtestSortableHeader('ClientStatus', protestClientStatusLabel)}
                          {renderProtestSortableHeader('AssignedTo', 'Assigned To')}
                          {renderProtestSortableHeader('AttachmentCount', 'Count')}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedProtestRecords.map((row, idx) => (
                          <tr key={row.ProtestID || row.AccountNumber || row.SigmaGeoNbr || idx}>
                            <td>{renderCountyName(row.SigmaGeoNbr)}</td>
                            <td>{formatDateOnly(row.ReceivedByTYP)}</td>
                            <td>{row.OwnerName || '-'}</td>
                            <td>{row.ProtestTypeDescription || renderProtestTypeName(row.ProtestTypeID)}</td>
                            <td>{formatCalendarDate(row.HearingDate)}</td>
                            {isAdmin && <td>{row.Status || renderStatusLabel(row.StatusCode || row.Status)}</td>}
                            <td>{row.ClientStatus || renderClientStatusLabel(row.ClientStatusCode || row.ClientStatus)}</td>
                            <td>{row.AssignedToFullName || row.AssignedFullName || row.AssignedTo || '-'}</td>
                            <td className="text-center">
                              {Number(row.AttachmentCount ?? 0) > 0 ? (
                                <button
                                  type="button"
                                  className="file-count-cell cao-count-button"
                                  onClick={() => handleOpenProtestAttachments(row)}
                                >
                                  {row.AttachmentCount}
                                </button>
                              ) : (
                                <span className="cao-count-value">0</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showProtestAttachmentsModal && (
        <div className="attachment-modal-overlay">
          <div className="attachment-modal cao-protest-attachment-modal">
            <h3 className="modal-title">Protest Attachments</h3>
            <button className="close-button" onClick={closeProtestAttachmentsModal}>×</button>

            {selectedProtestRow && (
              <div className="modal-heading">
                <span className="modal-owner-name">
                  {selectedProtestRow.OwnerName || `Protest ${selectedProtestRow.ProtestID}`}
                </span>
              </div>
            )}

            <p className="cao-protest-attachment-note">
              Previewable files open in-app with Back and Download controls. Use Download to save a copy to Downloads.
            </p>

            {protestAttachmentsLoading ? (
              <div className="spinner-container">
                <div className="spinner"></div>
                <p>Loading attachments...</p>
              </div>
            ) : protestAttachmentsError ? (
              <p className="owner-inline-error owner-inline-error--block">{protestAttachmentsError}</p>
            ) : protestAttachments.length === 0 ? (
              <p className="owner-no-records">No attachments found.</p>
            ) : (
              <>
                <div className="cao-protest-attachments-desktop">
                  <table className="owner-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>File Type</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {protestAttachments.map((attachment) => {
                        const descriptor = buildProtestAttachmentDescriptor(attachment);

                        return (
                          <tr key={attachment.AttachmentID}>
                            <td>{attachment.AttachmentTypeDescription || attachment.AttachmentType || '-'}</td>
                            <td>{friendlyFileType(attachment.FileType, attachment.FileName, attachment.FileExtension)}</td>
                            <td>{attachment.Description || '-'}</td>
                            <td>{renderFileActions(
                              descriptor,
                              'No File',
                              '',
                              handleFileAction,
                              { downloadIconOnly: true }
                            )}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="cao-protest-attachments-mobile">
                  {protestAttachments.map((attachment) => {
                    const descriptor = buildProtestAttachmentDescriptor(attachment);

                    return (
                      <article key={attachment.AttachmentID} className="cao-attachment-card">
                        <div className="cao-attachment-card__row">
                          <span className="cao-attachment-card__label">Type</span>
                          <span>{attachment.AttachmentTypeDescription || attachment.AttachmentType || '-'}</span>
                        </div>
                        <div className="cao-attachment-card__row">
                          <span className="cao-attachment-card__label">File Type</span>
                          <span>{friendlyFileType(attachment.FileType, attachment.FileName, attachment.FileExtension)}</span>
                        </div>
                        <div className="cao-attachment-card__row">
                          <span className="cao-attachment-card__label">Description</span>
                          <span>{attachment.Description || '-'}</span>
                        </div>
                        {renderFileActions(
                          descriptor,
                          'No File',
                          'cao-file-actions--stacked',
                          handleFileAction,
                          { downloadIconOnly: true }
                        )}
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {sharedViewerFile && (
        <HomeDocumentViewer
          file={sharedViewerFile}
          metadata={sharedViewerMetadata}
          loadDocument={loadSharedDocument}
          onClose={() => setSharedDocumentViewer(null)}
        />
      )}

      {documentViewer.open && !sharedViewerFile && (
        <div className="cao-document-viewer-overlay" role="dialog" aria-modal="true">
          <div className={`cao-document-viewer cao-document-viewer--${documentViewerAppearance}`}>
            <div className="cao-document-viewer__header">
              <button
                type="button"
                className="view-button cao-document-viewer__back"
                onClick={closeDocumentViewer}
                aria-label={documentViewer.backLabel}
              >
                <span aria-hidden="true">←</span>
                <span>Back</span>
              </button>

              <div className="cao-document-viewer__meta">
                <span className="cao-document-viewer__name">{documentViewer.fileName}</span>
                <span className="cao-document-viewer__type">
                  {documentViewer.fileTypeLabel}
                  {documentViewerPageCount
                    ? ` | Page ${Math.min(documentViewerCurrentPage, documentViewerPageCount)} of ${documentViewerPageCount}`
                    : isExcelViewerOpen && documentViewerSheetNames.length
                      ? ` | ${documentViewerSheetNames.length} Sheet${documentViewerSheetNames.length === 1 ? '' : 's'}`
                    : ''}
                </span>
              </div>

              <div className="cao-document-viewer__actions">
                <AppearanceSelector
                  value={documentViewerAppearance}
                  onChange={setDocumentViewerAppearance}
                  className="cao-document-viewer__appearance"
                  includeButtonClassName="view-button"
                  buttonClassName="cao-document-viewer__appearance-button"
                  activeButtonClassName="cao-document-viewer__appearance-button--active"
                />

                {isPdfViewerOpen && (
                  <div className="cao-document-viewer__zoom" aria-label="PDF zoom controls">
                    <button
                      type="button"
                      className="view-button cao-document-viewer__zoom-button"
                      onClick={() => adjustDocumentViewerScale('out')}
                      disabled={documentViewerScale <= MIN_PDF_SCALE}
                      aria-label="Zoom out"
                    >
                      -
                    </button>
                    <label className="cao-document-viewer__zoom-value" title="Enter zoom percentage">
                      <input
                        type="number"
                        min={Math.round(MIN_PDF_SCALE * 100)}
                        max={Math.round(MAX_PDF_SCALE * 100)}
                        step="1"
                        value={documentViewerZoomInput}
                        onChange={(event) => setDocumentViewerZoomInput(event.target.value)}
                        onBlur={applyDocumentViewerZoomInput}
                        onFocus={(event) => event.target.select()}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur();
                          if (event.key === 'Escape') {
                            setDocumentViewerZoomInput(String(Math.round(documentViewerScale * 100)));
                            event.currentTarget.blur();
                          }
                        }}
                        aria-label={`Zoom percentage, ${Math.round(MIN_PDF_SCALE * 100)} to ${Math.round(MAX_PDF_SCALE * 100)}`}
                      />
                      <span aria-hidden="true">%</span>
                    </label>
                    <button
                      type="button"
                      className="view-button cao-document-viewer__zoom-button"
                      onClick={() => adjustDocumentViewerScale('in')}
                      disabled={documentViewerScale >= MAX_PDF_SCALE}
                      aria-label="Zoom in"
                    >
                      +
                    </button>
                  </div>
                )}

                {isPdfViewerOpen && (
                  <button
                    type="button"
                    className="view-button cao-document-viewer__open-new-tab"
                    onClick={openDocumentViewerPdfInNewTab}
                    title="Open PDF in new tab"
                  >
                    Open PDF in new tab
                  </button>
                )}

                <button
                  type="button"
                  className="view-button cao-document-viewer__icon-action"
                  onClick={rotateDocumentViewer}
                  disabled={isExcelViewerOpen || isEmailViewerOpen}
                  aria-label="Rotate"
                  title="Rotate"
                >
                  <span aria-hidden="true">{'\u21BB'}</span>
                </button>

                <button
                  type="button"
                  className="view-button cao-document-viewer__download cao-document-viewer__icon-action"
                  disabled={pendingFileAction.key === documentViewer.resource?.key && pendingFileAction.mode === DOWNLOAD_ACTION}
                  onClick={() => handleFileAction(documentViewer.resource, DOWNLOAD_ACTION)}
                  aria-label="Download"
                  title="Download"
                >
                  <span aria-hidden="true">↓</span>
                </button>
              </div>
            </div>

            <div
              ref={documentViewerBodyRef}
              className={
                'cao-document-viewer__body'
                + (isPdfViewerOpen ? ' cao-document-viewer__body--pdf' : '')
                + (isPdfViewerOpen && !isLargePdfViewerOpen ? ' cao-document-viewer__body--pdf-page-view' : '')
                + (isExcelViewerOpen ? ' cao-document-viewer__body--excel' : '')
                + (isEmailViewerOpen ? ' cao-document-viewer__body--email' : '')
              }
            >
              {shouldShowDocumentViewerContext && (
                <DocumentMetadata
                  className="cao-document-viewer__context"
                  itemClassName="cao-document-viewer__context-row"
                  labelClassName="cao-document-viewer__context-label"
                  valueClassName="cao-document-viewer__context-value"
                  ariaLabel="Protest details"
                  items={[
                    { label: 'Owner Name', value: documentViewerOwnerName },
                    { label: 'Protest Type', value: documentViewerProtestType },
                    { label: 'Type', value: documentViewerAttachmentType, hidden: !documentViewerAttachmentType },
                    { label: 'Description', value: documentViewerAttachmentDescription, hidden: !documentViewerAttachmentDescription }
                  ]}
                />
              )}

              {shouldShowPdfTools && (
                <div className="cao-document-viewer__pdf-tools" aria-label="PDF tools">
                  <div className="cao-document-viewer__pdf-nav">
                    <div className="cao-document-viewer__pdf-nav-group cao-document-viewer__pdf-nav-group--page">
                      <button
                        type="button"
                        className="view-button"
                        onClick={() => goToDocumentViewerPage(documentViewerCurrentPage - 1)}
                        disabled={documentViewerCurrentPage <= 1 || isDocumentViewerPdfNavigationLocked}
                      >
                        Prev
                      </button>
                      <span className="cao-document-viewer__pdf-label">Page</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="cao-document-viewer__pdf-page-input"
                        value={documentViewerPageInput}
                        onChange={(e) => setDocumentViewerPageInput(e.target.value.replace(/[^\d]/g, ''))}
                        onBlur={handleDocumentViewerPageSubmit}
                        disabled={isDocumentViewerPdfNavigationLocked}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleDocumentViewerPageSubmit();
                          }
                        }}
                        aria-label="Go to PDF page"
                      />
                      <span className="cao-document-viewer__pdf-page-total">
                        of {documentViewerPageCount.toLocaleString()}
                      </span>
                      <button
                        type="button"
                        className="view-button"
                        onClick={() => goToDocumentViewerPage(documentViewerCurrentPage + 1)}
                        disabled={documentViewerCurrentPage >= documentViewerPageCount || isDocumentViewerPdfNavigationLocked}
                      >
                        Next
                      </button>
                    </div>

                    <div className="cao-document-viewer__pdf-nav-group cao-document-viewer__pdf-range-group">
                      <span className="cao-document-viewer__pdf-label">Pages</span>
                      <div className="cao-document-viewer__pdf-range-fields">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="cao-document-viewer__pdf-range-input"
                          value={documentViewerPdfRangeStartInput}
                          onChange={(e) => setDocumentViewerPdfRangeStartInput(e.target.value.replace(/[^\d]/g, ''))}
                          aria-label="Start PDF page"
                        />
                        <span className="cao-document-viewer__pdf-range-separator">to</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="cao-document-viewer__pdf-range-input"
                          value={documentViewerPdfRangeEndInput}
                          onChange={(e) => setDocumentViewerPdfRangeEndInput(e.target.value.replace(/[^\d]/g, ''))}
                          aria-label="End PDF page"
                        />
                      </div>
                      <div className="cao-document-viewer__pdf-range-actions">
                        <button
                          type="button"
                          className="view-button cao-document-viewer__pdf-range-action"
                          onClick={handleDocumentViewerPdfRangeDownload}
                          disabled={documentViewerPdfRangeActionMode === 'download' || documentViewerPdfRangeActionMode === 'print'}
                          aria-label={documentViewerPdfRangeActionMode === 'download' ? 'Preparing selected pages for download' : 'Download selected pages'}
                          title={documentViewerPdfRangeActionMode === 'download' ? 'Preparing selected pages for download' : 'Download selected pages'}
                        >
                          {documentViewerPdfRangeActionMode === 'download'
                            ? <span className="cao-document-viewer__pdf-range-busy" aria-hidden="true">...</span>
                            : renderPdfRangeActionIcon('download')}
                        </button>
                        <button
                          type="button"
                          className="view-button cao-document-viewer__pdf-range-action"
                          onClick={handleDocumentViewerPdfRangePrint}
                          disabled={documentViewerPdfRangeActionMode === 'download' || documentViewerPdfRangeActionMode === 'print'}
                          aria-label={documentViewerPdfRangeActionMode === 'print' ? 'Opening selected pages for printing' : 'Print selected pages'}
                          title={documentViewerPdfRangeActionMode === 'print' ? 'Opening selected pages for printing' : 'Print selected pages'}
                        >
                          {documentViewerPdfRangeActionMode === 'print'
                            ? <span className="cao-document-viewer__pdf-range-busy" aria-hidden="true">...</span>
                            : renderPdfRangeActionIcon('print')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {documentViewerPdfRangeError && (
                    <p className="cao-document-viewer__pdf-range-error">
                      {documentViewerPdfRangeError}
                    </p>
                  )}

                  {documentViewerPdfOwnerIndexState.status !== 'idle' && documentViewerPdfOwnerIndexState.status !== 'unsupported' && (
                    <div className={
                      'cao-document-viewer__pdf-owner-search'
                      + (isLargePdfViewerOpen ? ' cao-document-viewer__pdf-owner-search--compact' : '')
                    }>
                      <div className="cao-document-viewer__pdf-owner-header">
                        <span className="cao-document-viewer__pdf-label">Owner Search</span>
                        <span className="cao-document-viewer__pdf-owner-meta">
                          {documentViewerPdfOwnerIndexState.status === 'checking'
                            ? 'Checking whether owner search is available...'
                            : documentViewerPdfOwnerIndexState.status === 'indexing'
                              ? `Indexing owners: ${documentViewerPdfOwnerIndexState.scannedPages.toLocaleString()} / ${documentViewerPdfOwnerIndexState.totalPages.toLocaleString()} pages`
                              : documentViewerPdfOwnerIndexState.status === 'ready'
                                ? `${documentViewerPdfOwnerIndexState.entries.length.toLocaleString()} owners indexed`
                                : documentViewerPdfOwnerIndexState.error}
                        </span>
                      </div>

                      <input
                        type="text"
                        className="cao-document-viewer__pdf-owner-input"
                        value={documentViewerPdfOwnerQuery}
                        onChange={(e) => setDocumentViewerPdfOwnerQuery(e.target.value)}
                        placeholder={
                          documentViewerPdfOwnerIndexState.status === 'checking'
                            ? 'Checking owner layout...'
                            : 'Search by owner name'
                        }
                        disabled={documentViewerPdfOwnerIndexState.status === 'checking'}
                        aria-label="Search PDF by owner"
                      />

                      {documentViewerPdfOwnerDownloadError && (
                        <p className="cao-document-viewer__pdf-owner-error">
                          {documentViewerPdfOwnerDownloadError}
                        </p>
                      )}

                      {documentViewerPdfOwnerQuery.trim() && (
                        documentViewerPdfOwnerResults.length > 0 ? (
                          <ul className="cao-document-viewer__pdf-owner-results">
                            {documentViewerPdfOwnerResults.map((entry) => (
                              <li
                                key={getPdfOwnerResultKey(entry)}
                                className="cao-document-viewer__pdf-owner-result"
                              >
                                <button
                                  type="button"
                                  className="cao-document-viewer__pdf-owner-option"
                                  onClick={() => handleDocumentViewerOwnerJump(entry)}
                                >
                                  <span className="cao-document-viewer__pdf-owner-name">{entry.displayName}</span>
                                  <span className="cao-document-viewer__pdf-owner-pages">
                                    Pages {entry.startPage.toLocaleString()} - {entry.endPage.toLocaleString()}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="cao-document-viewer__pdf-owner-action"
                                  onClick={() => handleDocumentViewerOwnerDownload(entry)}
                                  disabled={!canDownloadOwnerPdfRanges || documentViewerPdfOwnerDownloadKey === getPdfOwnerResultKey(entry)}
                                  aria-label={`Download pages ${entry.startPage} to ${entry.endPage} for ${entry.displayName}`}
                                  title={
                                    canDownloadOwnerPdfRanges
                                      ? 'Download only these pages as a PDF'
                                      : 'Wait for owner indexing to finish before downloading a page range'
                                  }
                                >
                                  {documentViewerPdfOwnerDownloadKey === getPdfOwnerResultKey(entry) ? (
                                    <span>Preparing...</span>
                                  ) : (
                                    <>
                                      {renderFileActionIcon(DOWNLOAD_ACTION)}
                                      <span>Download PDF</span>
                                    </>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="cao-document-viewer__pdf-owner-empty">
                            {documentViewerPdfOwnerIndexState.status === 'indexing'
                              ? 'No owner match found in the indexed pages yet.'
                              : 'No owner match found.'}
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}

              {isPdfViewerOpen ? (
                <div className={
                  'cao-document-viewer__pdf-stage'
                  + (shouldShowPdfLoader ? ' cao-document-viewer__pdf-stage--loading' : '')
                  + (!isLargePdfViewerOpen ? ' cao-document-viewer__pdf-stage--page-view' : '')
                }>
                  {shouldShowPdfLoader && (
                    <div className="cao-document-viewer__pdf-loader" role="status" aria-live="polite">
                      <p className="cao-document-viewer__status">
                        {documentViewerPdfLoaderMessage}
                      </p>
                    </div>
                  )}
                  <Document
                    file={pdfViewerFile}
                    loading={null}
                    error={(
                      <p className="cao-document-viewer__status cao-document-viewer__status--error">
                        {documentViewerError || 'Unable to load PDF preview. Use Download to save this file.'}
                      </p>
                    )}
                    noData={<p className="cao-document-viewer__status">No PDF file available.</p>}
                    onLoadSuccess={handleDocumentViewerPdfLoadSuccess}
                    onLoadError={(err) => handleDocumentViewerPdfLoadFailure(err, 'Failed to load PDF preview:')}
                    onSourceError={(err) => handleDocumentViewerPdfLoadFailure(err, 'Failed to fetch PDF source:')}
                  >
                    {documentViewerRenderedPdfPageCount > 0 && (
                      <div className="cao-document-viewer__pdf">
                        {isLargePdfViewerOpen ? (
                          <PdfSideNavigation
                            currentPage={documentViewerCurrentPage}
                            pageCount={documentViewerPageCount}
                            disabled={isDocumentViewerPdfNavigationLocked}
                            onPrevious={() => goToDocumentViewerPage(documentViewerCurrentPage - 1)}
                            onNext={() => goToDocumentViewerPage(documentViewerCurrentPage + 1)}
                          >
                            <div
                              key={`pdf-page-${documentViewerCurrentPage}`}
                              ref={(node) => {
                                documentViewerPageRefs.current[documentViewerCurrentPage - 1] = node;
                              }}
                              className="cao-document-viewer__page"
                              data-page-number={documentViewerCurrentPage}
                            >
                              <Page
                                pageNumber={documentViewerCurrentPage}
                                renderMode={documentViewerAppearance === 'dark' ? 'custom' : 'canvas'}
                                customRenderer={DarkModePdfCanvas}
                                width={documentViewerWidth}
                                scale={documentViewerScale}
                                rotate={documentViewerRotation}
                                loading={renderPdfPagePlaceholder()}
                                onRenderSuccess={() => handleDocumentViewerPdfPageRenderSuccess(documentViewerCurrentPage)}
                                onRenderTextLayerSuccess={() => handleDocumentViewerPdfTextLayerRenderSuccess(documentViewerCurrentPage)}
                                onRenderTextLayerError={(err) => handleDocumentViewerPdfTextLayerRenderError(err, documentViewerCurrentPage)}
                                onRenderError={handleDocumentViewerPdfPageRenderError}
                                renderAnnotationLayer={false}
                                renderTextLayer
                              />
                            </div>
                          </PdfSideNavigation>
                        ) : (
                          <>
                            <PdfSideNavigation
                              currentPage={documentViewerCurrentPage}
                              pageCount={documentViewerPageCount}
                              disabled={isDocumentViewerPdfNavigationLocked}
                              onPrevious={() => goToDocumentViewerPage(documentViewerCurrentPage - 1)}
                              onNext={() => goToDocumentViewerPage(documentViewerCurrentPage + 1)}
                            >
                              <div
                                key={`pdf-page-${documentViewerDisplayedPdfPage}`}
                                ref={(node) => {
                                  documentViewerPageRefs.current[documentViewerDisplayedPdfPage - 1] = node;
                                }}
                                className="cao-document-viewer__page"
                                data-page-number={documentViewerDisplayedPdfPage}
                              >
                                <Page
                                  pageNumber={documentViewerDisplayedPdfPage}
                                  renderMode={documentViewerAppearance === 'dark' ? 'custom' : 'canvas'}
                                  customRenderer={DarkModePdfCanvas}
                                  width={documentViewerWidth}
                                  scale={documentViewerScale}
                                  rotate={documentViewerRotation}
                                  loading={renderPdfPagePlaceholder()}
                                  onRenderSuccess={() => handleDocumentViewerPdfPageRenderSuccess(documentViewerDisplayedPdfPage)}
                                  onRenderTextLayerSuccess={() => handleDocumentViewerPdfTextLayerRenderSuccess(documentViewerDisplayedPdfPage)}
                                  onRenderTextLayerError={(err) => handleDocumentViewerPdfTextLayerRenderError(err, documentViewerDisplayedPdfPage)}
                                  onRenderError={handleDocumentViewerPdfPageRenderError}
                                  renderAnnotationLayer={false}
                                  renderTextLayer
                                />
                              </div>
                            </PdfSideNavigation>

                            {documentViewerCurrentPage !== documentViewerDisplayedPdfPage && (
                              <div
                                key={`pdf-page-${documentViewerCurrentPage}`}
                                className="cao-document-viewer__page cao-document-viewer__page--preloading"
                                data-page-number={documentViewerCurrentPage}
                                aria-hidden="true"
                              >
                                <Page
                                  pageNumber={documentViewerCurrentPage}
                                  renderMode={documentViewerAppearance === 'dark' ? 'custom' : 'canvas'}
                                  customRenderer={DarkModePdfCanvas}
                                  width={documentViewerWidth}
                                  scale={documentViewerScale}
                                  rotate={documentViewerRotation}
                                  loading={renderPdfPagePlaceholder()}
                                  onRenderSuccess={() => handleDocumentViewerPdfPageRenderSuccess(documentViewerCurrentPage)}
                                  onRenderTextLayerSuccess={() => handleDocumentViewerPdfTextLayerRenderSuccess(documentViewerCurrentPage)}
                                  onRenderTextLayerError={(err) => handleDocumentViewerPdfTextLayerRenderError(err, documentViewerCurrentPage)}
                                  onRenderError={handleDocumentViewerPdfPageRenderError}
                                  renderAnnotationLayer={false}
                                  renderTextLayer
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </Document>
                </div>
              ) : isExcelViewerOpen ? (
                documentViewerLoading ? (
                  <p className="cao-document-viewer__status">Loading Excel preview...</p>
                ) : documentViewerError ? (
                  <p className="cao-document-viewer__status cao-document-viewer__status--error">
                    {documentViewerError}
                  </p>
                ) : !documentViewerSheetNames.length ? (
                  <p className="cao-document-viewer__status">No worksheet data available.</p>
                ) : (
                  <div className="cao-document-viewer__excel">
                    <div className="cao-document-viewer__excel-controls">
                      <div className="cao-document-viewer__excel-toolbar">
                        <div className="cao-document-viewer__excel-sheet-picker">
                          <span className="cao-document-viewer__excel-label">Sheet</span>
                          {documentViewerSheetNames.length > 1 ? (
                            <select
                              className="cao-document-viewer__excel-select"
                              value={documentViewerActiveSheetName}
                              onChange={(e) => handleDocumentViewerSheetChange(e.target.value)}
                              aria-label="Select worksheet"
                            >
                              {documentViewerSheetNames.map((sheetName) => (
                                <option key={sheetName} value={sheetName}>{sheetName}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="cao-document-viewer__excel-sheet-name">{documentViewerActiveSheetName}</span>
                          )}
                        </div>

                        <div className="cao-document-viewer__excel-toolbar-right">
                          <span className="cao-document-viewer__excel-summary">
                            {documentViewerFilteredExcelRows.length}
                            {documentViewerExcelActiveFilters.length ? ` of ${documentViewerExcelBodyRows.length}` : ''}
                            {' '}rows | {documentViewerExcelColumnCount} columns
                          </span>
                        </div>
                      </div>

                    </div>

                    {documentViewerExcelRows.length > 0 && documentViewerExcelColumnCount > 0 ? (
                      <div
                        ref={documentViewerExcelWrapRef}
                        className="cao-document-viewer__excel-wrap"
                      >
                        <table className="cao-document-viewer__excel-table">
                          <thead>
                            <tr>
                              <th className="cao-document-viewer__excel-corner" aria-hidden="true"></th>
                              {Array.from({ length: documentViewerExcelColumnCount }, (_, columnIndex) => (
                                <th
                                  key={`excel-column-${columnIndex}`}
                                  className="cao-document-viewer__excel-column"
                                  aria-sort={
                                    documentViewerExcelSortConfig.column === columnIndex
                                      ? (documentViewerExcelSortConfig.direction === 'asc' ? 'ascending' : 'descending')
                                      : 'none'
                                  }
                                >
                                  <button
                                    type="button"
                                    className={
                                      'cao-document-viewer__excel-sort-button'
                                      + (documentViewerExcelSortConfig.column === columnIndex
                                        ? ' cao-document-viewer__excel-sort-button--active'
                                        : '')
                                    }
                                    onClick={() => handleDocumentViewerExcelSort(columnIndex)}
                                    aria-label={
                                      documentViewerExcelSortConfig.column === columnIndex
                                        ? `Sort ${getExcelHeaderCellText(documentViewerExcelHeaderRow, columnIndex) || `column ${getExcelColumnLabel(columnIndex)}`} ${documentViewerExcelSortConfig.direction === 'asc' ? 'descending' : 'ascending'}`
                                        : `Sort ${getExcelHeaderCellText(documentViewerExcelHeaderRow, columnIndex) || `column ${getExcelColumnLabel(columnIndex)}`} ascending`
                                    }
                                  >
                                    <span className="cao-document-viewer__excel-sort-letter">
                                      {getExcelColumnLabel(columnIndex)}
                                    </span>
                                    <span className="cao-document-viewer__excel-sort-title">
                                      {getExcelHeaderCellText(documentViewerExcelHeaderRow, columnIndex) || `Column ${getExcelColumnLabel(columnIndex)}`}
                                    </span>
                                    {documentViewerExcelSortConfig.column === columnIndex && (
                                      <span className="cao-document-viewer__excel-sort-indicator" aria-hidden="true">
                                        {documentViewerExcelSortConfig.direction === 'asc' ? UP_ARROW : DOWN_ARROW}
                                      </span>
                                    )}
                                  </button>
                                </th>
                              ))}
                            </tr>
                            <tr className="cao-document-viewer__excel-filter-row">
                              <th className="cao-document-viewer__excel-filter-index">Search</th>
                              {Array.from({ length: documentViewerExcelColumnCount }, (_, columnIndex) => (
                                <th key={`excel-filter-${columnIndex}`} className="cao-document-viewer__excel-filter-cell">
                                  <input
                                    type="text"
                                    className="cao-document-viewer__excel-filter-input"
                                    value={documentViewerExcelFilters[columnIndex] || ''}
                                    onChange={(e) => handleDocumentViewerExcelFilterChange(columnIndex, e.target.value)}
                                    placeholder={getExcelHeaderCellText(documentViewerExcelHeaderRow, columnIndex) || getExcelColumnLabel(columnIndex)}
                                    aria-label={`Search ${getExcelHeaderCellText(documentViewerExcelHeaderRow, columnIndex) || `column ${getExcelColumnLabel(columnIndex)}`}`}
                                  />
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {documentViewerSortedExcelRows.length > 0 ? (
                              documentViewerSortedExcelRows.map(({ row, sheetRowNumber }) => {
                                const safeRow = row;
                                return (
                                  <tr key={`excel-row-${sheetRowNumber}`}>
                                    <th className="cao-document-viewer__excel-row">{sheetRowNumber}</th>
                                    {Array.from({ length: documentViewerExcelColumnCount }, (_, columnIndex) => {
                                      const cellValue = safeRow[columnIndex];
                                      return (
                                        <td
                                          key={`excel-cell-${sheetRowNumber}-${columnIndex}`}
                                          className="cao-document-viewer__excel-cell"
                                        >
                                          {cellValue === '' || cellValue === null || cellValue === undefined
                                            ? '\u00A0'
                                            : String(cellValue)}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <th className="cao-document-viewer__excel-row">-</th>
                                <td
                                  className="cao-document-viewer__excel-empty"
                                  colSpan={documentViewerExcelColumnCount}
                                >
                                  {documentViewerExcelBodyRows.length > 0
                                    ? 'No rows match the current search.'
                                    : 'This worksheet does not contain previewable cell data.'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="cao-document-viewer__status">This worksheet does not contain previewable cell data.</p>
                    )}
                  </div>
                )
              ) : isEmailViewerOpen ? (
                documentViewerLoading ? (
                  <p className="cao-document-viewer__status">Loading email preview...</p>
                ) : documentViewerError ? (
                  <p className="cao-document-viewer__status cao-document-viewer__status--error">
                    {documentViewerError}
                  </p>
                ) : documentViewerEmailPreview ? (
                  <div className="cao-document-viewer__email">
                    <div className="cao-document-viewer__email-card">
                      <div className="cao-document-viewer__email-header">
                        <span className="cao-document-viewer__email-label">Subject</span>
                        <h4 className="cao-document-viewer__email-subject">
                          {documentViewerEmailPreview.subject || '(No subject)'}
                        </h4>
                      </div>

                      <div className="cao-document-viewer__email-fields">
                        <div className="cao-document-viewer__email-field">
                          <span className="cao-document-viewer__email-label">From</span>
                          <span className="cao-document-viewer__email-value">
                            {documentViewerEmailPreview.from || '-'}
                          </span>
                        </div>
                        <div className="cao-document-viewer__email-field">
                          <span className="cao-document-viewer__email-label">To</span>
                          <span className="cao-document-viewer__email-value">
                            {documentViewerEmailPreview.to || '-'}
                          </span>
                        </div>
                        {documentViewerEmailPreview.cc && (
                          <div className="cao-document-viewer__email-field">
                            <span className="cao-document-viewer__email-label">Cc</span>
                            <span className="cao-document-viewer__email-value">
                              {documentViewerEmailPreview.cc}
                            </span>
                          </div>
                        )}
                        {documentViewerEmailPreview.bcc && (
                          <div className="cao-document-viewer__email-field">
                            <span className="cao-document-viewer__email-label">Bcc</span>
                            <span className="cao-document-viewer__email-value">
                              {documentViewerEmailPreview.bcc}
                            </span>
                          </div>
                        )}
                        {documentViewerEmailPreview.date && (
                          <div className="cao-document-viewer__email-field">
                            <span className="cao-document-viewer__email-label">Date</span>
                            <span className="cao-document-viewer__email-value">
                              {documentViewerEmailPreview.date}
                            </span>
                          </div>
                        )}
                      </div>

                      {documentViewerEmailPreview.attachments.length > 0 && (
                        <div className="cao-document-viewer__email-attachments">
                          <span className="cao-document-viewer__email-label">Attachments</span>
                          <ul className="cao-document-viewer__email-attachment-list">
                            {documentViewerEmailPreview.attachments.map((attachment, index) => (
                              <li
                                key={`${attachment.fileName}-${attachment.mimeType}-${index}`}
                                className="cao-document-viewer__email-attachment-item"
                              >
                                <span className="cao-document-viewer__email-attachment-name">
                                  {attachment.fileName}
                                </span>
                                <span className="cao-document-viewer__email-attachment-meta">
                                  {attachment.mimeType || 'Attachment'}
                                  {attachment.size > 0 ? ` | ${attachment.size.toLocaleString()} bytes` : ''}
                                </span>
                                {renderFileActions(
                                  attachment.descriptor,
                                  'No attachment',
                                  'cao-file-actions--icon-only cao-document-viewer__email-attachment-actions',
                                  handleEmailAttachmentAction,
                                  { iconOnly: true }
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="cao-document-viewer__email-message">
                        <span className="cao-document-viewer__email-label">Message</span>
                        <pre className="cao-document-viewer__email-body-text">
                          {documentViewerEmailPreview.body || 'No email body available.'}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="cao-document-viewer__status">No email preview data available.</p>
                )
              ) : documentViewer.mimeType.startsWith('image/') ? (
                <img
                  src={documentViewer.previewUrl}
                  alt={documentViewer.fileName}
                  className="cao-document-viewer__image"
                  style={{ transform: `rotate(${documentViewerRotation}deg)` }}
                />
              ) : (
                <iframe
                  src={documentViewer.previewUrl}
                  title={documentViewer.fileName}
                  className="cao-document-viewer__frame"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaoPage;
